import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import CloseIcon from "@mui/icons-material/Close";
import * as Yup from "yup";
import { loadStripe } from "@stripe/stripe-js";
import validator from "validator";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import SecurityIcon from "@mui/icons-material/Security";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PaymentIcon from "@mui/icons-material/Payment";
import SendIcon from "@mui/icons-material/Send";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";

import {
  Modal,
  Box,
  Typography,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Grid2 as Grid,
  TextField,
  Checkbox,
  Button,
  Link,
  Chip,
  Alert,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  useMediaQuery,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";
import { styled } from "@mui/material/styles";
import CreditCardDisplay from "../noc/CreditCardDisplay";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  isAllowedSubAdminRoute,
  isSubAdminEmail,
} from "../../functions/helperFunctions";
import {
  getPermitPackages,
  updatePermitPackage,
} from "../../store/actions/dataActions";
import { formatMoneyValue } from "../../functions/permitFormHelpers";
import {
  setActiveTab,
  setModalsState,
  setPage,
  setRowCount,
  setSelectedRow,
} from "../../store/actions/uiActions/myPermitsActions";
import NotarizationRequestConfirmationModal from "./NotarizationRequestConfirmation";
import { omitCustomProps } from "../../utils/omitCustomProps";
import {
  isCurrentFormNOC,
  isNocAffidavitDoc,
} from "../permitForms/permitFormHelpers";
import {
  getDocumentFieldValueByName,
  getSharedFieldValueFromDocuments,
} from "../../utils/documentFieldPayload";
import {
  getDocumentSigningRequirements,
  getPartyRequirementDisplay,
  getSignerOverrideToggleLocks,
  hasPartyToggleableRequirements,
} from "./notarizationRequirementHelpers";
import { getOwnerDetailFieldKind } from "../permitForms/utils/ownerDetailHelpers";

// Styled Components
const AnimatedModal = styled(Modal)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    backdropFilter: "none",
  },
}));

const ModalContainer = styled(
  Box,
  omitCustomProps("isMobile"),
)(({ theme, isMobile }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  height: isMobile ? "100vh" : "90vh",
  maxHeight: "850px",
  width: isMobile ? "95vw" : "85vw",
  maxWidth: "1100px",
  backgroundColor: theme.palette.background.paper,
  borderRadius: "16px",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

const ModalHeader = styled(
  Box,
  omitCustomProps("isMobile"),
)(({ theme, isMobile }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  padding: isMobile ? theme.spacing(1.5) : theme.spacing(2),
  position: "relative",
  overflow: "hidden",
  flexShrink: 0,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)",
    // Disable shimmer animation to reduce paint work on open
    animation: "none",
  },
}));

const StepCard = styled(
  Card,
  omitCustomProps("active"),
)(({ theme, active }) => ({
  marginBottom: theme.spacing(2),
  transition: "all 0.3s ease",
  cursor: "pointer",
  border: active
    ? `2px solid ${theme.palette.primary.main}`
    : `2px solid transparent`,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[8],
  },
  ...(active && {
    background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.main}08 100%)`,
  }),
}));

const AnimatedStep = styled(Box)(() => ({
  minHeight: "450px",
  display: "flex",
  flexDirection: "column",
}));

const ProgressIndicator = styled(
  Box,
  omitCustomProps("isMobile"),
)(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: isMobile ? theme.spacing(0.75) : theme.spacing(1.5),
  background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
  borderRadius: "8px",
  border: `1px solid ${theme.palette.grey[200]}`,
  flexShrink: 0,
}));

const StepIcon = styled(
  Box,
  omitCustomProps("completed", "active"),
)(({ theme, completed, active }) => ({
  width: 48,
  height: 48,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginRight: theme.spacing(2),
  transition: "all 0.3s ease",
  backgroundColor: completed
    ? theme.palette.success.main
    : active
      ? theme.palette.primary.main
      : theme.palette.grey[300],
  color:
    completed || active
      ? theme.palette.primary.contrastText
      : theme.palette.grey[600],
  // Remove pulsing animation to reduce layout/paint cycles
}));

const FloatingActionButton = styled(LoadingButton)(({ theme }) => ({
  borderRadius: "8px",
  padding: theme.spacing(0.75, 2.5),
  fontSize: "0.95rem",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  },
}));

const OWNER_NAME_REGEX = /^[A-Za-z ]+$/;
const OWNER_NAME_ERROR_MESSAGE =
  "Only letters and spaces are allowed in owner name fields";
const OWNER_BUSINESS_NAME_ERROR_MESSAGE =
  "Company or entity names cannot be used in the owner signer personal name fields";

const getDocumentDisplayLabel = (document, index) =>
  document?.label ||
  document?.document_name ||
  document?.name ||
  `Requirement ${index + 1}`;

const OWNER_BUSINESS_KEYWORDS = [
  "INC",
  "LLC",
  "CORP",
  "CORPORATION",
  "COMPANY",
  "CO",
  "LP",
  "LLP",
  "LTD",
  "LIMITED",
  "PARTNERSHIP",
  "PARTNERS",
  "ASSOCIATES",
  "ASSOCIATION",
  "ENTERPRISES",
  "GROUP",
  "HOLDINGS",
  "PROPERTIES",
  "REALTY",
  "TRUST",
  "TRUSTEE",
];

const hasOwnerBusinessKeyword = (value) => {
  const normalizedValue = (value || "").trim().toUpperCase();
  if (!normalizedValue) return false;

  return OWNER_BUSINESS_KEYWORDS.some((keyword) => {
    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`(^|\\s)${escapedKeyword}($|\\s)`, "i").test(
      normalizedValue,
    );
  });
};

const hasInvalidOwnerBusinessNameValue = (value) =>
  hasOwnerBusinessKeyword(value);

const getOwnerNameFieldError = (formik, fieldName, ownerRequired) => {
  if (!ownerRequired) return false;
  return Boolean(formik.touched[fieldName] && formik.errors[fieldName]);
};

const getOwnerNameFieldHelperText = (formik, fieldName, ownerRequired) => {
  if (!ownerRequired) return "";
  if (formik.touched[fieldName] && formik.errors[fieldName]) {
    return formik.errors[fieldName];
  }
  return "Only letters and spaces allowed";
};

const hasInvalidOwnerNameValue = (value) => {
  if (!value) return false;
  return !OWNER_NAME_REGEX.test(value);
};

function StartNotarizationModal({
  open,
  setOpen,
  setReviewDocModalOpen,
  setSnackOpen,
  setRonScheduleOpen,
  docNotaryTypeValue,
  // setAddPaymentModalOpen,
}) {
  const extraSmall = useMediaQuery((theme) => theme.breakpoints.down("xsm"));
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);
  const { subAdmins, default_form_fields: defaultFormFields = [] } =
    useSelector((state) => state.nocData || {});
  const { subAdminDomains = [], subAdminPages = [] } = subAdmins || {};
  const { selectedRow } = useSelector((state) => state.myPermitsComponent);

  // Enterprise / Branch users never need to add a payment card for notarization,
  // because their service tiers are $0 (the backend also enforces this).
  // Note: depending on where the user object was built, these flags can exist
  // either at `user.enterprise.*` or at the top-level user document.
  const isEnterpriseUser = Boolean(
    currentUser?.user?.enterprise?.is_enterprise ||
      currentUser?.user?.is_enterprise,
  );
  const isBranchUser = Boolean(
    currentUser?.user?.enterprise?.is_branch || currentUser?.user?.is_branch,
  );
  const isOrgUser = isEnterpriseUser || isBranchUser;

  const dispatch = useDispatch();

  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  // Core state
  const [signingOrder, setSigningOrder] = useState("contractor");
  const [autoStartRecording, setAutoStartRecording] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [notaryPaymentLoading, setNotaryPaymentLoading] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  // Wizard state
  const [currentWizardStep, setCurrentWizardStep] = useState(0);
  const [selectedNotary, setSelectedNotary] = useState(null);
  const [availableNotaries, setAvailableNotaries] = useState([]);
  const [submitError, setSubmitError] = useState("");
  const [commissionErrors, setCommissionErrors] = useState({});
  const [paymentCards, setPaymentCards] = useState([]);
  const [confirmedPricing, setConfirmedPricing] = useState(null);
  const [remotePaymentChoice, setRemotePaymentChoice] = useState("");
  const [partyToggleLoading, setPartyToggleLoading] = useState("");

  // Signing requirements
  const [ownerSigning, setOwnerSigning] = useState({
    notarizationNeeded: false,
    signingNeeded: false,
  });
  const [contractorSigning, setContractorSigning] = useState({
    notarizationNeeded: false,
    signingNeeded: false,
  });

  // IPEN & Notary Pool states - use refs to persist across selectedRow updates during auto-save
  const notarizationTypeRef = useRef("ron");
  const ownerNotarizationTypeRef = useRef("ron");
  const ownerRonNotarySourceRef = useRef("permitRockstar");
  const contractorRonNotarySourceRef = useRef("permitRockstar");
  const ipenPinRef = useRef("");
  const ownerIpenNotaryIdRef = useRef("");

  const [notarizationType, setNotarizationTypeState] = useState("ron");
  const [ownerNotarizationType, setOwnerNotarizationTypeState] =
    useState("ron");
  // Single contractor state (for non-St. Johns counties)
  const [contractorNotarizationType, setContractorNotarizationTypeState] =
    useState("ron");
  const [contractorIpenNotaryId, setContractorIpenNotaryIdState] = useState("");
  const [ownerRequireIpenPin, setOwnerRequireIpenPin] = useState(false);
  const [contractorRequireIpenPin, setContractorRequireIpenPin] =
    useState(false);
  // Store contractor settings per contractor (keyed by license number) - only for St. Johns
  const [contractorSettings, setContractorSettings] = useState({}); // { licenseNumber: { notarizationType: "ron", ipenNotaryId: "", ronNotarySource: "permitRockstar", selectedUser: "", requireIpenPin: true } }
  const [ownerRonNotarySource, setOwnerRonNotarySourceState] =
    useState("permitRockstar");
  const [contractorRonNotarySource, setContractorRonNotarySourceState] =
    useState("permitRockstar");
  const [ipenPin, setIpenPinState] = useState("");
  const [hasOrgNotaries, setHasOrgNotaries] = useState(false);
  const [canUseIpen, setCanUseIpen] = useState(false);
  const [ownerIpenNotaryId, setOwnerIpenNotaryIdState] = useState("");

  const validateSelectedIpenNotaries = useCallback(
    async (payload) => {
      const validations = [];
      const seenNotaryIds = new Set();

      const addValidation = (notaryId, notaryEmail) => {
        const normalizedId = (notaryId || "").trim();
        const normalizedEmail = (notaryEmail || "").trim().toLowerCase();

        if (!normalizedId || seenNotaryIds.has(normalizedId)) return;

        seenNotaryIds.add(normalizedId);
        validations.push({
          notary_id: normalizedId,
          notary_email: normalizedEmail,
        });
      };

      if (payload?.owner_notarization_type === "ipen") {
        const ownerNotary = availableNotaries.find(
          (item) => item?.notary_id === payload?.owner_ipen_notary_id,
        );
        addValidation(payload?.owner_ipen_notary_id, ownerNotary?.email);
      }

      if (isStJohnsCounty()) {
        Object.values(payload?.selected_contractors || {}).forEach(
          (settings) => {
            if (settings?.notarizationType === "ipen") {
              const contractorNotary = availableNotaries.find(
                (item) => item?.notary_id === settings?.ipenNotaryId,
              );
              addValidation(settings?.ipenNotaryId, contractorNotary?.email);
            }
          },
        );
      } else if (payload?.contractor_notarization_type === "ipen") {
        const contractorNotary = availableNotaries.find(
          (item) => item?.notary_id === payload?.contractor_ipen_notary_id,
        );
        addValidation(
          payload?.contractor_ipen_notary_id,
          contractorNotary?.email,
        );
      }

      for (const validation of validations) {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/blue-notary/validate-ipen-notary`,
          {
            ...validation,
            currentSession,
          },
          { withCredentials: true },
        );

        if (!resp?.data?.success) {
          throw new Error(
            resp?.data?.message ||
              "Selected IPEN notary is no longer valid in BlueNotary.",
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableNotaries, currentSession],
  );

  const [requiredContractors, setRequiredContractors] = useState([]);
  const [currentContractorIndex, setCurrentContractorIndex] = useState(0);
  const [contractorsSeen, setContractorsSeen] = useState(new Set());
  // Store form values per contractor (keyed by license number) to preserve user edits

  useEffect(() => {
    if (selectedRow?.county?.toLowerCase() !== "st johns") return;

    let foundContractors = [];
    const seenSelectedUsers = new Set();

    const contractors = Object.values(selectedRow?.documents ?? {})
      .map((d) => d?.contractor)
      .filter(Boolean);

    if (contractors.length <= 1) {
      foundContractors.push(selectedRow?.contractor);
      setContractorsSeen(true);
    } else {
      if (Array.isArray(currentUser?.user?.contractors)) {
        currentUser?.user?.contractors?.forEach((c) => {
          if (contractors.includes(c?.licenseNumber)) {
            const selectedUser = c?.selectedUser;
            // Only push if selectedUser is unique (not seen before)
            if (selectedUser && !seenSelectedUsers.has(selectedUser)) {
              seenSelectedUsers.add(selectedUser);
              foundContractors.push(c);
            } else if (!selectedUser) {
              // If no selectedUser, allow it (might be main user's contractor)
              foundContractors.push(c);
            }
          }
        });
      }
    }

    setRequiredContractors(foundContractors);
    // Reset to first contractor when contractors change
    setCurrentContractorIndex(0);
    // Reset viewed contractors - mark first one as seen
    if (foundContractors.length > 0) {
      setContractorsSeen(new Set([0]));
      // Initialize contractor settings with default values for all contractors
      setContractorSettings((prev) => {
        const updated = { ...prev };
        foundContractors.forEach((contractor) => {
          const license = contractor?.licenseNumber;
          if (license && !updated[license]) {
            updated[license] = {
              notarizationType: "ron",
              ipenNotaryId: "",
              ronNotarySource: "permitRockstar",
              selectedUser: contractor?.selectedUser || "",
              requireIpenPin: true,
            };
          } else if (
            license &&
            updated[license] &&
            !updated[license].selectedUser
          ) {
            // Preserve existing settings but add selectedUser if missing
            updated[license] = {
              ...updated[license],
              ronNotarySource:
                updated[license].ronNotarySource || "permitRockstar",
              selectedUser: contractor?.selectedUser || "",
              requireIpenPin:
                typeof updated[license].requireIpenPin === "boolean"
                  ? updated[license].requireIpenPin
                  : true,
            };
          } else if (license && updated[license]) {
            updated[license] = {
              ...updated[license],
              requireIpenPin:
                typeof updated[license].requireIpenPin === "boolean"
                  ? updated[license].requireIpenPin
                  : true,
            };
          }
        });
        return updated;
      });
    } else {
      setContractorsSeen(new Set());
      setContractorSettings({});
    }
  }, [
    currentUser?.user?.contractors,
    selectedRow?.contractor,
    selectedRow?.county,
    selectedRow?.documents,
    open,
  ]);

  const resetCommissionErrors = useCallback(() => {
    setCommissionErrors({});
    setSubmitError("");
  }, []);

  // Wrapper setters that update both state and refs
  const setNotarizationType = (value) => {
    notarizationTypeRef.current = value;
    setNotarizationTypeState(value);
  };
  const setOwnerNotarizationType = (value) => {
    resetCommissionErrors();
    ownerNotarizationTypeRef.current = value;
    setOwnerNotarizationTypeState(value);
  };
  const setOwnerRonNotarySource = (value) => {
    ownerRonNotarySourceRef.current = value;
    setOwnerRonNotarySourceState(value);
  };
  // Helper function to check if St. Johns county
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isStJohnsCounty = useCallback(() => {
    return (
      selectedRow?.county?.toLowerCase() === "st johns" ||
      selectedRow?.county?.toLowerCase() === "st_johns" ||
      selectedRow?.county?.toLowerCase()?.includes("st johns")
    );
  }, [selectedRow?.county]);

  // Helper functions to get/set contractor settings (only for St. Johns)
  const getCurrentContractorLicense = useCallback(() => {
    if (!isStJohnsCounty()) return "";
    return requiredContractors[currentContractorIndex]?.licenseNumber || "";
  }, [currentContractorIndex, isStJohnsCounty, requiredContractors]);

  const getContractorNotarizationType = useCallback(() => {
    if (!isStJohnsCounty()) {
      return contractorNotarizationType;
    }
    const license = getCurrentContractorLicense();
    return contractorSettings[license]?.notarizationType || "ron";
  }, [
    contractorNotarizationType,
    contractorSettings,
    getCurrentContractorLicense,
    isStJohnsCounty,
  ]);

  const getContractorIpenNotaryId = () => {
    if (!isStJohnsCounty()) {
      return contractorIpenNotaryId;
    }

    // For St. Johns with multiple contractors: return the shared notary (from first contractor that has one)
    if (requiredContractors.length > 1) {
      // Find the first contractor that has a notary selected
      for (const contractor of requiredContractors) {
        const license = contractor?.licenseNumber;
        if (license && contractorSettings[license]?.ipenNotaryId) {
          return contractorSettings[license].ipenNotaryId;
        }
      }
      return "";
    }

    // Single contractor: return that contractor's notary
    const license = getCurrentContractorLicense();
    return contractorSettings[license]?.ipenNotaryId || "";
  };

  const getContractorRonNotarySource = () => {
    if (!isStJohnsCounty()) {
      return contractorRonNotarySource;
    }
    const license = getCurrentContractorLicense();
    return contractorSettings[license]?.ronNotarySource || "permitRockstar";
  };

  const getContractorRequireIpenPin = () => {
    if (!isStJohnsCounty()) {
      return contractorRequireIpenPin;
    }

    const license = getCurrentContractorLicense();
    return contractorSettings[license]?.requireIpenPin ?? true;
  };

  const setContractorNotarizationType = (value) => {
    resetCommissionErrors();
    if (!isStJohnsCounty()) {
      setContractorNotarizationTypeState(value);
      return;
    }
    const license = getCurrentContractorLicense();
    if (!license) return;

    setContractorSettings((prev) => ({
      ...prev,
      [license]: {
        ...prev[license],
        notarizationType: value,
      },
    }));
  };
  const setContractorIpenNotaryId = useCallback(
    (value) => {
      resetCommissionErrors();
      if (!isStJohnsCounty()) {
        setContractorIpenNotaryIdState(value);
        return;
      }

      // For St. Johns with multiple contractors: apply the same notary to all contractors
      if (requiredContractors.length > 1) {
        setContractorSettings((prev) => {
          const updated = { ...prev };
          // Update all contractors with the same notary
          requiredContractors.forEach((contractor) => {
            const license = contractor?.licenseNumber;
            if (license) {
              updated[license] = {
                ...updated[license],
                ipenNotaryId: value,
              };
            }
          });
          return updated;
        });
      } else {
        // Single contractor: update only that contractor
        const license = getCurrentContractorLicense();
        if (!license) return;

        setContractorSettings((prev) => ({
          ...prev,
          [license]: {
            ...prev[license],
            ipenNotaryId: value,
          },
        }));
      }
    },
    [
      getCurrentContractorLicense,
      isStJohnsCounty,
      requiredContractors,
      resetCommissionErrors,
    ],
  );
  const setContractorRonNotarySource = (value) => {
    if (!isStJohnsCounty()) {
      contractorRonNotarySourceRef.current = value;
      setContractorRonNotarySourceState(value);
      return;
    }

    const license = getCurrentContractorLicense();
    if (!license) return;

    setContractorSettings((prev) => ({
      ...prev,
      [license]: {
        ...prev[license],
        ronNotarySource: value,
      },
    }));
  };
  const setContractorRequireIpenPinValue = (value) => {
    if (!isStJohnsCounty()) {
      setContractorRequireIpenPin(value);
      return;
    }

    const license = getCurrentContractorLicense();
    if (!license) return;

    setContractorSettings((prev) => ({
      ...prev,
      [license]: {
        ...prev[license],
        requireIpenPin: value,
      },
    }));
  };
  const setIpenPin = (value) => {
    ipenPinRef.current = value;
    setIpenPinState(value);
  };
  const setOwnerIpenNotaryId = useCallback(
    (value) => {
      resetCommissionErrors();
      ownerIpenNotaryIdRef.current = value;
      setOwnerIpenNotaryIdState(value);
    },
    [ownerIpenNotaryIdRef, resetCommissionErrors],
  );
  // Restore state from refs when selectedRow changes but modal is still open
  // This prevents state loss during auto-save updates
  useEffect(() => {
    if (open && selectedRow) {
      // Restore values from refs if they differ from current state
      if (notarizationTypeRef.current !== notarizationType) {
        setNotarizationTypeState(notarizationTypeRef.current);
      }
      if (ownerNotarizationTypeRef.current !== ownerNotarizationType) {
        setOwnerNotarizationTypeState(ownerNotarizationTypeRef.current);
      }
      if (ownerRonNotarySourceRef.current !== ownerRonNotarySource) {
        setOwnerRonNotarySourceState(ownerRonNotarySourceRef.current);
      }
      if (contractorRonNotarySourceRef.current !== contractorRonNotarySource) {
        setContractorRonNotarySourceState(contractorRonNotarySourceRef.current);
      }
      if (ipenPinRef.current !== ipenPin) {
        setIpenPinState(ipenPinRef.current);
      }
      if (ownerIpenNotaryIdRef.current !== ownerIpenNotaryId) {
        setOwnerIpenNotaryIdState(ownerIpenNotaryIdRef.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    selectedRow?.task_id, // Only react to task_id changes, not entire object
    notarizationType,
    ownerNotarizationType,
    ownerRonNotarySource,
    contractorRonNotarySource,
    ipenPin,
    ownerIpenNotaryId,
  ]);

  const orgNotaryData = useMemo(
    () =>
      currentUser?.user?.tier?.notary ||
      currentUser?.subuser?.tier?.notary ||
      currentUser?.tier?.notary ||
      {},
    [currentUser],
  );

  const orgNotaryUser = useMemo(
    () =>
      (currentUser?.subuser
        ? currentUser?.subuser?.notary_user
        : currentUser?.user?.notary_user) ?? null,
    [currentUser],
  );

  const organizationName = useMemo(
    () => orgNotaryData?.orgName || "Organization",
    [orgNotaryData],
  );

  const hasRonOrgNotaries = useMemo(() => {
    const subscriptions = orgNotaryData?.subscriptions || [];
    return subscriptions.some(
      (subscription) =>
        subscription?.notary_type === 1 || subscription?.notary_type === 2,
    );
  }, [orgNotaryData]);

  const orgNotarySubscriptions = useMemo(
    () => orgNotaryData?.subscriptions || [],
    [orgNotaryData],
  );

  // Service selection states
  const [planReviewService, setPlanReviewService] = useState({
    usePermitRockstar: false,
    useOrganization: false,
    customProvider: "",
  });
  const [inspectionService, setInspectionService] = useState({
    usePermitRockstar: false,
    useOrganization: false,
    customProvider: "",
    inspectionTypes: [],
  });

  // Loading states
  const [loadingNotaries, setLoadingNotaries] = useState(false);
  const [signOnly, setSignOnly] = useState(false);

  const isAdminUser =
    currentUser?.user?.role === "admin" || Boolean(currentUser?.user?.admin);
  const isSubAdminUser = isSubAdminEmail(
    currentUser?.user?.email,
    subAdminDomains,
  );
  const isAllowedCustomersPackagesSubAdmin = isAllowedSubAdminRoute({
    email: currentUser?.user?.email,
    subAdminDomains,
    subAdminPages,
    path: "/permits/customers-packages",
  });
  const isRemoteConnectSession =
    Boolean(currentUser?.admin_remote) ||
    sessionStorage.getItem("adminRemote") === "true";
  const isCustomerPackagesAdminFlow =
    (isAdminUser || (isSubAdminUser && isAllowedCustomersPackagesSubAdmin)) &&
    typeof window !== "undefined" &&
    window.location.pathname.includes("/permits/customers-packages");

  const defaultPaymentCard = useMemo(() => {
    if (!Array.isArray(paymentCards) || paymentCards.length === 0) return null;
    return (
      paymentCards.find((card) => card?.default) || paymentCards[0] || null
    );
  }, [paymentCards]);

  const ownerSigningRef = useRef(ownerSigning);
  const contractorSigningRef = useRef(contractorSigning);

  useEffect(() => {
    if (orgNotaryUser) {
      const hasUserInSubscriptionList = orgNotarySubscriptions?.find(
        (orgNotary) => orgNotary?.user_id === orgNotaryUser?.notary_id,
      );

      if (hasUserInSubscriptionList) {
        if (ownerNotarizationType === "ipen" && hasOrgNotaries)
          setOwnerIpenNotaryId(orgNotaryUser?.notary_id);

        if (getContractorNotarizationType() === "ipen" && hasOrgNotaries)
          setContractorIpenNotaryId(orgNotaryUser?.notary_id);
      }
    }
  }, [
    orgNotaryUser,
    orgNotarySubscriptions,
    hasOrgNotaries,
    ownerNotarizationType,
    getContractorNotarizationType,
    setContractorIpenNotaryId,
    setOwnerIpenNotaryId,
  ]);

  useEffect(() => {
    // Check if all documents are sign-only (only sign: true, all other properties false)
    if (
      !selectedRow?.selected_documents ||
      selectedRow.selected_documents.length === 0
    ) {
      return;
    }

    let isSignOnly = true;

    // Iterate through all selected documents
    for (const document of selectedRow.selected_documents) {
      const signatures = document?.signatures;

      if (!signatures) {
        continue;
      }

      // Iterate through all signature parties (owner, contractor, private_provider, examiner, etc.)
      for (const [partyName, partyData] of Object.entries(signatures)) {
        // Skip 'order' and other non-party properties
        if (
          partyName === "order" ||
          typeof partyData !== "object" ||
          partyData === null
        ) {
          continue;
        }

        // Only check parties that have sign: true
        // If sign is false, we don't care about other properties
        if (partyData.sign !== true) {
          continue;
        }

        // For parties with sign: true, check all other properties (notary, etc.)
        // They should all be false for it to be sign-only
        const otherProperties = Object.entries(partyData).filter(
          ([key]) => key !== "sign",
        );

        const hasOtherTrueValues = otherProperties.some(
          ([, value]) => value === true,
        );

        // If sign is true but any other property is also true, it's not sign-only
        if (hasOtherTrueValues) {
          isSignOnly = false;
          break;
        }
      }

      // If we found a document that's not sign-only, break early
      if (!isSignOnly) {
        break;
      }
    }

    setSignOnly(isSignOnly);
  }, [selectedRow]);

  useEffect(() => {
    ownerSigningRef.current = ownerSigning;
  }, [ownerSigning]);

  useEffect(() => {
    contractorSigningRef.current = contractorSigning;
  }, [contractorSigning]);

  const mapping = useMemo(() => ["notary_record", "noc_notary"], []);

  // Get filtered documents based on docNotaryTypeValue
  const getFilteredDocuments = useCallback(() => {
    let selected_documents = [];

    if (docNotaryTypeValue === 0) {
      selected_documents = selectedRow?.selected_documents || [];
    } else if (docNotaryTypeValue === 1) {
      selected_documents =
        selectedRow?.selected_documents?.filter(
          (doc, index) =>
            isCurrentFormNOC(selectedRow, selectedRow?.permit_type, index) ||
            isNocAffidavitDoc(doc),
        ) || [];
    } else if (docNotaryTypeValue === 2) {
      selected_documents =
        selectedRow?.selected_documents?.filter(
          (doc, index) =>
            !isCurrentFormNOC(selectedRow, selectedRow?.permit_type, index),
        ) || [];
    }

    return selected_documents;
  }, [docNotaryTypeValue, selectedRow?.selected_documents]);

  // Memoize and defer document list to reduce work during initial open
  const filteredDocsMemo = React.useMemo(
    () => getFilteredDocuments(),
    [getFilteredDocuments],
  );
  const deferredFilteredDocs = React.useDeferredValue(filteredDocsMemo);

  // Wizard configuration - base steps
  const baseWizardSteps = useMemo(
    () => [
      {
        id: "overview",
        title: "Document Overview",
        icon: <AssignmentIcon />,
        description: "Review document requirements and signing order",
      },
      {
        id: "services",
        title: "Notarization Method",
        icon: <SecurityIcon />,
        description: "Choose notarization method for each party",
      },
      {
        id: "information",
        title: "Signer Information",
        icon: <PersonIcon />,
        description: "Provide signer details",
      },
      {
        id: "payment",
        title: "Payment & Confirmation",
        icon: <PaymentIcon />,
        description: "Complete payment and confirm details",
      },
    ],
    [],
  );

  // Filter steps based on whether notarization is needed
  const needsNotarization =
    contractorSigning?.notarizationNeeded || ownerSigning?.notarizationNeeded;
  const wizardSteps = useMemo(() => {
    if (needsNotarization) {
      return baseWizardSteps;
    }
    // If only signing is needed, skip notarization method and notary steps
    return baseWizardSteps.filter((step) => !step.requiresNotarization);
  }, [needsNotarization, baseWizardSteps]);

  // Get instruction text based on signing requirements
  const getInstructionText = useCallback(() => {
    const ownerHasAction =
      ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded;
    const contractorHasNotarization = contractorSigning?.notarizationNeeded;

    if (contractorHasNotarization && ownerHasAction) {
      if (selectedRow?.documents?.noc_affidavit?.is_created) {
        return "Both parties have actions. Because the Notice of Commencement affidavit is included, the owner must complete their notarization first; the contractor proceeds after.";
      }
      return "Both parties have actions. Choose who signs first and the other party will be invited automatically after the first completes.";
    }

    if (contractorHasNotarization && !ownerHasAction) {
      return "Only the contractor needs to sign and notarize the required documents. The owner/agent has no actions for these PDFs.";
    }

    if (!ownerHasAction && !contractorSigning?.signingNeeded) {
      return "Contractors are only required to sign documents. Upon providing some basic information, you can begin signing your documents.";
    }

    if (
      !contractorHasNotarization &&
      contractorSigning?.signingNeeded &&
      ownerHasAction
    ) {
      return "Contractors only need to e‑sign. After they sign, we will email the owner/agent to complete Remote Online Notarization.";
    }

    if (!contractorHasNotarization && !contractorSigning?.signingNeeded) {
      return "Only the owner/agent needs to complete notarization. We will provide instructions once the notarization method has been selected.";
    }

    return "Please review the document requirements and select your preferred notarization method.";
  }, [
    ownerSigning,
    contractorSigning,
    selectedRow?.documents?.noc_affidavit?.is_created,
  ]);

  const getPartyEnabledState = useCallback(
    (party) =>
      getFilteredDocuments().some((doc) => {
        const partySignature = doc?.signatures?.[party] || {};
        return Boolean(partySignature.sign || partySignature.notary);
      }),
    [getFilteredDocuments],
  );

  const getPartyHasToggleableRequirements = useCallback(
    (party) =>
      getFilteredDocuments().some((doc) =>
        hasPartyToggleableRequirements(doc, party),
      ),
    [getFilteredDocuments],
  );

  const ownerPartyEnabled = getPartyEnabledState("owner");
  const contractorPartyEnabled = getPartyEnabledState("contractor");
  const ownerHasToggleableRequirements =
    getPartyHasToggleableRequirements("owner");
  const contractorHasToggleableRequirements =
    getPartyHasToggleableRequirements("contractor");
  const signerOverrideToggleLocks = useMemo(
    () =>
      getSignerOverrideToggleLocks(
        filteredDocsMemo,
        selectedRow?.documents || {},
      ),
    [filteredDocsMemo, selectedRow?.documents],
  );
  const selectedNocDocument = useMemo(
    () =>
      (selectedRow?.selected_documents || []).find(
        (doc) => doc?.is_noc === true,
      ),
    [selectedRow?.selected_documents],
  );
  const hasSelectedNoc = Boolean(selectedNocDocument);
  const nocHasActiveNotarization = Boolean(
    selectedNocDocument?.signatures?.owner?.notary ||
      selectedNocDocument?.signatures?.contractor?.notary,
  );

  useEffect(() => {
    if (!open) return;

    const savedAutoRecordNoc = currentUser?.subuser
      ? currentUser?.subuser?.autoRecordNoc
      : currentUser?.user?.autoRecordNoc;

    setAutoStartRecording(
      Boolean(savedAutoRecordNoc) && Boolean(nocHasActiveNotarization),
    );
  }, [open, currentUser, nocHasActiveNotarization]);

  const handleToggleDocumentParty = useCallback(
    async (party) => {
      if (signerOverrideToggleLocks[party]) {
        setSubmitError(
          "This signer is controlled by the document's signer override selection.",
        );
        return;
      }

      const isOwner = party === "owner";
      const isCurrentlyEnabled = isOwner
        ? ownerPartyEnabled
        : contractorPartyEnabled;
      const otherPartyEnabled = isOwner
        ? contractorPartyEnabled
        : ownerPartyEnabled;

      if (isCurrentlyEnabled && !otherPartyEnabled) {
        setSubmitError("At least one party must remain active.");
        return;
      }

      setPartyToggleLoading(party);
      setSubmitError("");

      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/toggle-selected-document-party`,
          {
            task_id: selectedRow?.task_id,
            currentSession,
            party,
            enabled: !isCurrentlyEnabled,
            documents_type: docNotaryTypeValue,
          },
          { withCredentials: true },
        );

        if (resp?.data?.success && resp?.data?.data) {
          await dispatch(updatePermitPackage(resp.data.data));
          await dispatch(setSelectedRow(resp.data.data));
        }
      } catch (error) {
        setSubmitError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to update party requirements.",
        );
      } finally {
        setPartyToggleLoading("");
      }
    },
    [
      contractorPartyEnabled,
      currentSession,
      dispatch,
      docNotaryTypeValue,
      ownerPartyEnabled,
      signerOverrideToggleLocks,
      selectedRow?.task_id,
    ],
  );

  const renderPartyRequirementChip = useCallback((doc, party) => {
    const requirement = getPartyRequirementDisplay(doc, party);
    const isDisabled = requirement.disabled;

    const chipSx = {
      fontSize: "0.7rem",
      height: 20,
      ...(isDisabled && {
        opacity: 1,
        bgcolor: "grey.400",
        color: "grey.900",
        borderColor: "grey.500",
        textDecoration: "line-through",
        "& .MuiChip-label": {
          textDecoration: "line-through",
          color: "grey.900",
        },
      }),
    };

    if (requirement.label === "Notarize") {
      return (
        <Chip label="Notarize" size="small" color="secondary" sx={chipSx} />
      );
    }

    if (requirement.label === "Sign") {
      return <Chip label="Sign" size="small" color="info" sx={chipSx} />;
    }

    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }, []);

  useEffect(() => {
    const hasNotaries = (orgNotaryData?.num_notaries || 0) > 0;
    setHasOrgNotaries(hasNotaries);
  }, [orgNotaryData]);

  useEffect(() => {
    let ownerNotarizationNeeded = false;
    let ownerSigningNeeded = false;
    let contractorNotarizationNeeded = false;
    let contractorSigningNeeded = false;

    let selected_documents = [];

    if (docNotaryTypeValue === 0) {
      selected_documents = selectedRow?.selected_documents || [];
    } else if (docNotaryTypeValue === 1) {
      selected_documents =
        selectedRow?.selected_documents?.filter(
          (doc, index) =>
            isCurrentFormNOC(selectedRow, selectedRow?.permit_type, index) ||
            isNocAffidavitDoc(doc),
        ) || [];
    } else if (docNotaryTypeValue === 2) {
      selected_documents =
        selectedRow?.selected_documents?.filter(
          (doc, index) =>
            !isCurrentFormNOC(selectedRow, selectedRow?.permit_type, index),
        ) || [];
    }

    selected_documents.forEach((doc) => {
      const requirements = getDocumentSigningRequirements(doc);
      if (requirements.ownerSign) ownerSigningNeeded = true;
      if (requirements.ownerNotary) ownerNotarizationNeeded = true;
      if (requirements.contractorSign) contractorSigningNeeded = true;
      if (requirements.contractorNotary) contractorNotarizationNeeded = true;
    });

    const newOwner = {
      notarizationNeeded: ownerNotarizationNeeded,
      signingNeeded: ownerSigningNeeded,
    };

    const newContractor = {
      notarizationNeeded: contractorNotarizationNeeded,
      signingNeeded: contractorSigningNeeded,
    };

    if (JSON.stringify(ownerSigningRef.current) !== JSON.stringify(newOwner)) {
      setOwnerSigning(newOwner);
    }

    if (
      JSON.stringify(contractorSigningRef.current) !==
      JSON.stringify(newContractor)
    ) {
      setContractorSigning(newContractor);
    }
  }, [docNotaryTypeValue, selectedRow, open]);

  useEffect(() => {
    let cards = [];

    if (currentUser?.payment_cards?.length > 0) {
      cards = currentUser.payment_cards.map((card) => {
        const lastTwo = String(card.exp_year).slice(-2);
        const first = card.exp_month;

        return {
          id: card.id,
          cardNum: "**** **** **** " + card.last4,
          cardName: card.name ? card.name : card.card_holder,
          expires:
            (first < 10 ? "0" + String(first) : String(first)) + "/" + lastTwo,
          default: card.default,
        };
      });
      setPaymentCards(cards);
    }
  }, [currentUser.payment_cards]);

  useEffect(() => {
    // Reset backend-confirmed pricing when selected notarization tab changes
    setConfirmedPricing(null);
  }, [docNotaryTypeValue, open]);

  useEffect(() => {
    if (!open) {
      setRemotePaymentChoice("");
      return;
    }

    if (isRemoteConnectSession && !isAdminUser && paymentCards.length === 0) {
      setRemotePaymentChoice("skip");
      return;
    }

    if (!isRemoteConnectSession) {
      setRemotePaymentChoice("");
    }
  }, [open, isRemoteConnectSession, isAdminUser, paymentCards.length]);

  const discountPricing = useMemo(() => {
    // Sign-only flow does not support coupon logic currently.
    if (signOnly) {
      return {
        hasDiscount: false,
        originalPrice: Number.isFinite(
          parseFloat(currentUser?.service_tiers?.sign_only),
        )
          ? parseFloat(currentUser?.service_tiers?.sign_only)
          : null,
        finalPrice: null,
      };
    }

    if (![0, 1].includes(docNotaryTypeValue)) {
      return {
        hasDiscount: false,
        originalPrice: null,
        finalPrice: null,
      };
    }

    const discountKey = mapping[docNotaryTypeValue];
    const userDiscount = currentUser?.user?.discount?.[discountKey];
    const tierDiscount = currentUser?.tier?.discount?.[discountKey];
    const selectedDiscount = userDiscount || tierDiscount;

    const isDiscountValid =
      !!selectedDiscount?.active &&
      !!selectedDiscount?.id &&
      (selectedDiscount?.duration === "forever" ||
        (selectedDiscount?.duration === "once" &&
          selectedDiscount?.used === false));

    const originalPrice = parseFloat(currentUser?.service_tiers?.[discountKey]);
    const discountAmount = parseFloat(selectedDiscount?.amount);

    if (
      !isDiscountValid ||
      !Number.isFinite(originalPrice) ||
      !Number.isFinite(discountAmount)
    ) {
      return {
        hasDiscount: false,
        originalPrice: Number.isFinite(originalPrice) ? originalPrice : null,
        finalPrice: null,
      };
    }

    return {
      hasDiscount: true,
      originalPrice,
      finalPrice: Math.max(0, originalPrice - discountAmount),
    };
  }, [
    docNotaryTypeValue,
    mapping,
    signOnly,
    currentUser?.service_tiers,
    currentUser?.tier?.discount,
    currentUser?.user?.discount,
  ]);

  const isZeroTotalByCoupon = useMemo(() => {
    if (signOnly) {
      return false;
    }

    if (confirmedPricing?.has_discount) {
      return Number(confirmedPricing?.final_price) === 0;
    }

    return (
      discountPricing?.hasDiscount === true &&
      Number(discountPricing?.finalPrice) === 0
    );
  }, [confirmedPricing, discountPricing, signOnly]);

  const isNoChargeUser =
    isAdminUser || currentUser?.user?.role === "admin" || isOrgUser;
  const showsZeroTotalWithoutPayment =
    isNoChargeUser || isCustomerPackagesAdminFlow;

  // Update signing order based on requirements
  useEffect(() => {
    const ownerRequired =
      ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded;
    const contractorRequired =
      contractorSigning?.notarizationNeeded || contractorSigning?.signingNeeded;

    if (ownerRequired && !contractorRequired) {
      setSigningOrder("owner");
      return;
    }
    if (!ownerRequired && contractorRequired) {
      setSigningOrder("contractor");
      return;
    }

    // Edge case: NOC affidavit present forces owner first when both required
    if (
      contractorSigning?.notarizationNeeded &&
      selectedRow?.documents?.noc_affidavit?.is_created
    ) {
      setSigningOrder("owner");
      return;
    }
  }, [
    ownerSigning,
    contractorSigning,
    selectedRow?.documents?.noc_affidavit?.is_created,
  ]);

  const handleNewPhoneChange = (event) => {
    const { value, name } = event.target;

    // Remove all non-numeric characters
    const digitsOnly = value.replace(/\D/g, "");

    // Limit to 10 digits (XXX-XXX-XXXX format)
    if (digitsOnly.length > 10) {
      return; // Don't update if more than 10 digits
    }

    let formattedPhoneNumber = "";

    // Format as XXX-XXX-XXXX
    for (let i = 0; i < digitsOnly.length; i++) {
      if (i === 3 || i === 6) {
        formattedPhoneNumber += "-";
      }
      formattedPhoneNumber += digitsOnly[i];
    }

    formik.setFieldValue(name, formattedPhoneNumber); // Update formik field value
  };

  const collectSelectedIpenNotaryIds = useCallback(() => {
    const selectedIds = new Set();

    if (
      ownerSigning?.notarizationNeeded &&
      ownerNotarizationType === "ipen" &&
      ownerIpenNotaryId
    ) {
      selectedIds.add(ownerIpenNotaryId);
    }

    if (contractorSigning?.notarizationNeeded) {
      if (isStJohnsCounty()) {
        requiredContractors.forEach((contractor) => {
          const license = contractor?.licenseNumber;
          if (!license) return;
          const settings = contractorSettings[license];
          if (settings?.notarizationType === "ipen" && settings?.ipenNotaryId) {
            selectedIds.add(settings.ipenNotaryId);
          }
        });
      } else if (
        contractorNotarizationType === "ipen" &&
        contractorIpenNotaryId
      ) {
        selectedIds.add(contractorIpenNotaryId);
      }
    }

    return Array.from(selectedIds);
  }, [
    ownerSigning,
    ownerNotarizationType,
    ownerIpenNotaryId,
    contractorSigning,
    contractorNotarizationType,
    contractorIpenNotaryId,
    contractorSettings,
    requiredContractors,
    isStJohnsCounty,
  ]);

  function convertDayjsToObject(dayjsObj) {
    return {
      day_of_month: dayjsObj.date(),
      month: dayjsObj.month() + 1,
      year: dayjsObj.year(),
      time: dayjsObj.format("HH:mm"),
    };
  }

  useEffect(() => {
    if (submitError) {
      const interval = setTimeout(() => {
        setSubmitError("");
      }, 4000);

      return () => clearTimeout(interval);
    }
  }, [submitError]);

  // Get available organization notaries
  const getAvailableNotaries = useCallback(async () => {
    if (notarizationType !== "ipen") return;

    setLoadingNotaries(true);
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/get-org-notaries`,
        {
          params: { currentSession },
          withCredentials: true,
        },
      );

      const notaries = resp.data.notaries || [];
      setAvailableNotaries(notaries);

      // If we found notaries but hasOrgNotaries was false, update it
      if (notaries.length > 0 && !hasOrgNotaries) {
        setHasOrgNotaries(true);
      }
    } catch (error) {
      console.error("Error fetching organization notaries:", error);
      console.error("Error details:", error.response?.data);
      setAvailableNotaries([]);
    } finally {
      setLoadingNotaries(false);
    }
  }, [hasOrgNotaries, notarizationType, currentSession]);

  // Determine if IPEN can be used based on document requirements
  useEffect(() => {
    // IPEN can only be used if:
    // 1. Organization has notaries
    // 2. Owner doesn't need notarization OR it's a mixed scenario

    const ownerOnlyNotarization =
      ownerSigning.notarizationNeeded &&
      !contractorSigning.notarizationNeeded &&
      !contractorSigning.signingNeeded;

    // IPEN is available if org has notaries and it's NOT owner-only notarization
    setCanUseIpen(hasOrgNotaries && !ownerOnlyNotarization);

    // Reset to RON if IPEN becomes unavailable
    if (!hasOrgNotaries && notarizationType === "ipen") {
      setNotarizationType("ron");
    }
  }, [hasOrgNotaries, ownerSigning, contractorSigning, notarizationType]);

  // Reset owner and contractor notarization types to RON if IPEN is selected but org has no notaries
  useEffect(() => {
    if (!hasOrgNotaries) {
      if (ownerNotarizationType === "ipen") {
        setOwnerNotarizationType("ron");
      }
      if (isStJohnsCounty()) {
        // Reset all contractors to RON if IPEN is not available (St. Johns)
        setContractorSettings((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((license) => {
            if (updated[license]?.notarizationType === "ipen") {
              updated[license] = {
                ...updated[license],
                notarizationType: "ron",
                ipenNotaryId: "",
              };
            }
          });
          return updated;
        });
      } else {
        // Reset single contractor to RON if IPEN is not available (other counties)
        if (contractorNotarizationType === "ipen") {
          setContractorNotarizationType("ron");
          setContractorIpenNotaryId("");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasOrgNotaries,
    ownerNotarizationType,
    contractorNotarizationType,
    selectedRow?.county, // isStJohnsCounty depends on this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  // Generate IPEN PIN when IPEN is selected
  useEffect(() => {
    const generateIpenPin = async () => {
      if (notarizationType === "ipen" && open && !ipenPin) {
        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/notary/generate-ipen-pin`,
            {
              task_id: selectedRow?.task_id,
              currentSession: currentSession,
            },
            { withCredentials: true },
          );

          setIpenPin(resp.data.pin);
        } catch (error) {
          console.error("Error generating IPEN PIN:", error);
          // Fallback PIN generation (frontend only, for display) - numbers only
          setIpenPin(Math.floor(100000 + Math.random() * 900000).toString());
        }
      }
    };

    generateIpenPin();
  }, [notarizationType, open, selectedRow?.task_id, currentSession, ipenPin]);

  // Fetch available notaries when IPEN is selected
  useEffect(() => {
    const notaryStepIndex = wizardSteps.findIndex(
      (step) => step.id === "notary",
    );

    if (
      notarizationType === "ipen" &&
      currentWizardStep === notaryStepIndex &&
      notaryStepIndex !== -1
    ) {
      getAvailableNotaries();
    }
  }, [notarizationType, currentWizardStep, getAvailableNotaries, wizardSteps]);

  // Helper function to split full name into first, middle, and last name
  // Name order is now: firstname + middlename + lastname (normalized by backend)
  // Backend now returns names in natural order: "First Middle Last"
  const splitFullName = (fullName) => {
    if (!fullName || typeof fullName !== "string") {
      return { first: "", middle: "", last: "" };
    }

    const trimmedName = fullName.trim();
    if (!trimmedName) {
      return { first: "", middle: "", last: "" };
    }

    // Check if it's a business name (has business indicators like LLC, INC, etc.)
    // Business names should not be split
    const isBusiness = hasOwnerBusinessKeyword(trimmedName);

    // If it's a business name, don't split it - use as full name
    if (isBusiness) {
      return { first: "", middle: "", last: trimmedName };
    }

    // For individual names, split assuming "First Middle Last" format
    const parts = trimmedName.split(/\s+/).filter((p) => p.trim());

    if (parts.length === 0) {
      return { first: "", middle: "", last: "" };
    } else if (parts.length === 1) {
      // Only one part - assume it's the first name
      return { first: parts[0], middle: "", last: "" };
    } else if (parts.length === 2) {
      // Two parts: firstname + lastname
      return { first: parts[0], middle: "", last: parts[1] };
    } else {
      // Three or more parts: firstname + middlename(s) + lastname
      return {
        first: parts[0],
        middle: parts.slice(1, -1).join(" "),
        last: parts[parts.length - 1],
      };
    }
  };

  const refs = {
    oName: useRef(null),
    oFirst: useRef(null),
    oMiddle: useRef(null),
    oLast: useRef(null),
    oAddress: useRef(null),
    oCity: useRef(null),
    oZip: useRef(null),
    cFirst: useRef(null),
    cLast: useRef(null),
    cAddress: useRef(null),
    cCity: useRef(null),
    cZip: useRef(null),
    cEmail: useRef(null),
    oEmail: useRef(null),
    cDOB: useRef(null),
    cMobile: useRef(null),
    oMobile: useRef(null),
    cIdType: useRef(null),
  };

  const scrollToFirstError = (errors) => {
    const fields = Object.keys(errors);
    // Find the first field with an error and scroll to it
    for (const field of fields) {
      if (refs[field]?.current) {
        refs[field].current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
        break;
      }
    }
  };

  // Wizard navigation functions
  const validateSelectedNotaryCommissions = useCallback(async () => {
    const selectedIds = collectSelectedIpenNotaryIds();
    if (selectedIds.length === 0) {
      return { success: true, hasErrors: false };
    }

    setNotaryPaymentLoading(true);
    resetCommissionErrors();
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/validate-notary-commissions`,
        {
          notary_ids: selectedIds,
          currentSession,
        },
        { withCredentials: true },
      );

      const results = resp?.data?.results || [];
      const errorsMap = {};
      results.forEach((result) => {
        if (result?.is_expired && result?.notary_id) {
          errorsMap[result.notary_id] =
            result?.message ||
            "Commission date is expired and needs to update.";
        }
      });

      if (Object.keys(errorsMap).length > 0) {
        setCommissionErrors(errorsMap);
        setSubmitError(
          "One or more selected notaries have an expired commission. Please update the notary before continuing.",
        );
        return { success: true, hasErrors: true };
      }

      return { success: true, hasErrors: false };
    } catch (error) {
      console.error("Error validating notary commissions:", error);
      setSubmitError(
        "Unable to validate notary commissions. Please try again.",
      );
      return { success: false, hasErrors: true };
    } finally {
      setNotaryPaymentLoading(false);
    }
  }, [collectSelectedIpenNotaryIds, currentSession, resetCommissionErrors]);

  const handleNext = useCallback(async () => {
    // Validate notary selection for IPEN in "Notarization Method" step
    const currentStep = wizardSteps[currentWizardStep];
    if (currentStep?.id === "services") {
      // Check if all contractors have been viewed (only for St. Johns with multiple contractors)
      if (
        isStJohnsCounty() &&
        requiredContractors.length > 1 &&
        contractorsSeen.size < requiredContractors.length
      ) {
        setSubmitError(
          "Please navigate through all contractors using the arrow buttons",
        );
        return;
      }
      setCurrentContractorIndex(0);
      if (
        ownerSigning?.notarizationNeeded &&
        ownerNotarizationType === "ipen" &&
        hasOrgNotaries &&
        !ownerIpenNotaryId
      ) {
        // Check if owner needs IPEN notary selection
        setSubmitError(
          "Please select a notary for Property Owner IPEN notarization",
        );
        return;
      }
      // Check if contractor needs IPEN notary selection
      if (contractorSigning?.notarizationNeeded && hasOrgNotaries) {
        if (isStJohnsCounty()) {
          // Multi-contractor logic for St. Johns - check if any contractor needs IPEN and if shared notary is selected
          if (requiredContractors.length > 1) {
            // For multiple contractors, they all share the same notary
            // Check if any contractor has IPEN selected and if shared notary is set
            let hasIpenWithoutNotary = false;
            for (const contractor of requiredContractors) {
              const license = contractor?.licenseNumber;
              if (!license) continue;

              const settings = contractorSettings[license];
              if (settings?.notarizationType === "ipen") {
                if (!settings?.ipenNotaryId) {
                  hasIpenWithoutNotary = true;
                  break;
                }
              }
            }
            if (hasIpenWithoutNotary) {
              setSubmitError(
                "Please select a notary for Contractor IPEN notarization (applies to all contractors)",
              );
              return;
            }
          } else {
            // Single contractor in St. Johns
            for (const contractor of requiredContractors) {
              const license = contractor?.licenseNumber;
              if (!license) continue;

              const settings = contractorSettings[license];
              if (
                settings?.notarizationType === "ipen" &&
                !settings?.ipenNotaryId
              ) {
                setSubmitError(
                  `Please select a notary for ${contractor.businessName || contractor.dba_name || "Contractor"} IPEN notarization`,
                );
                return;
              }
            }
          }
        } else {
          // Single contractor logic for other counties
          if (
            contractorNotarizationType === "ipen" &&
            !contractorIpenNotaryId
          ) {
            setSubmitError(
              "Please select a notary for Contractor IPEN notarization",
            );
            return;
          }
        }
      }

      const shouldValidateCommissions =
        (ownerSigning?.notarizationNeeded &&
          ownerNotarizationType === "ipen") ||
        (contractorSigning?.notarizationNeeded &&
          (isStJohnsCounty()
            ? requiredContractors.some((contractor) => {
                const license = contractor?.licenseNumber;
                if (!license) return false;
                return contractorSettings[license]?.notarizationType === "ipen";
              })
            : contractorNotarizationType === "ipen"));

      if (shouldValidateCommissions) {
        const validation = await validateSelectedNotaryCommissions();
        if (!validation.success || validation.hasErrors) {
          return;
        }
      }
    }

    // Validate notary selection for IPEN in old "notary" step (if it exists)
    if (
      currentStep?.id === "notary" &&
      notarizationType === "ipen" &&
      hasOrgNotaries &&
      !selectedNotary
    ) {
      setSubmitError("Please select a notary for IPEN notarization");
      return;
    }

    setSubmitError("");
    setCurrentWizardStep((prev) => Math.min(prev + 1, wizardSteps.length - 1));
  }, [
    wizardSteps,
    currentWizardStep,
    ownerSigning,
    contractorSigning,
    ownerNotarizationType,
    ownerIpenNotaryId,
    contractorNotarizationType,
    contractorIpenNotaryId,
    notarizationType,
    hasOrgNotaries,
    selectedNotary,
    contractorsSeen,
    requiredContractors,
    contractorSettings,
    isStJohnsCounty,
    validateSelectedNotaryCommissions,
  ]);

  const handlePrevious = useCallback(() => {
    setCurrentWizardStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleStepClick = useCallback(
    (stepIndex) => {
      if (stepIndex <= currentWizardStep) {
        setCurrentWizardStep(stepIndex);
      }
    },
    [currentWizardStep],
  );

  // Reset modal state
  const resetModalState = useCallback(
    (formikRef) => {
      setCurrentWizardStep(0);
      setSelectedNotary(null);
      setAvailableNotaries([]);
      setSubmitError("");
      setIsAcknowledged(false);
      setNotarizationType("ron");
      setOwnerRonNotarySource("permitRockstar");
      setContractorRonNotarySource("permitRockstar");
      setIpenPin("");
      setOwnerIpenNotaryId("");
      setOwnerRequireIpenPin(true);
      setContractorNotarizationType("ron");
      setContractorIpenNotaryId("");
      setContractorRequireIpenPin(true);
      setContractorSettings({}); // Reset all contractor settings
      setConfirmedPricing(null);
      resetCommissionErrors();
      // Reset service selections
      setPlanReviewService({
        usePermitRockstar: false,
        useOrganization: false,
        customProvider: "",
      });
      setInspectionService({
        usePermitRockstar: false,
        useOrganization: false,
        customProvider: "",
        inspectionTypes: [],
      });
      // Reset loading states
      setLoadingNotaries(false);
      if (formikRef) {
        formikRef.resetForm();
      }
      // setContractorNotarizationType and setContractorIpenNotaryId are wrapper functions
      // that call stable state setters, so they're safe to omit from dependencies
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resetCommissionErrors],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        oName: Yup.string(), // Keep for backward compatibility, but use individual fields for validation
        oFirst:
          ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded
            ? Yup.string()
                .required("First name is required")
                .matches(OWNER_NAME_REGEX, OWNER_NAME_ERROR_MESSAGE)
            : Yup.string(),
        oMiddle:
          ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded
            ? Yup.string().test(
                "owner-middle-characters",
                OWNER_NAME_ERROR_MESSAGE,
                function (value) {
                  if (!value) return true;
                  return OWNER_NAME_REGEX.test(value);
                },
              )
            : Yup.string(),
        oLast:
          ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded
            ? Yup.string()
                .required("Last name is required")
                .matches(OWNER_NAME_REGEX, OWNER_NAME_ERROR_MESSAGE)
            : Yup.string(),
        oAddress: ownerSigning?.notarizationNeeded
          ? Yup.string().required("Address is required")
          : Yup.string(),
        oCity: ownerSigning?.notarizationNeeded
          ? Yup.string().required("City is required")
          : Yup.string(),
        oZip: ownerSigning?.notarizationNeeded
          ? Yup.string()
              .required("Zip Code is required")
              .test("zipCode", "Invalid zip code", function (value) {
                return validator.isPostalCode(value, "US");
              })
          : Yup.string(),
        cFirst:
          contractorSigning?.notarizationNeeded ||
          contractorSigning?.signingNeeded
            ? Yup.string().required("First name is required")
            : Yup.string(),
        cLast:
          contractorSigning?.notarizationNeeded ||
          contractorSigning?.signingNeeded
            ? Yup.string().required("Last name is required")
            : Yup.string(),
        cAddress: contractorSigning?.notarizationNeeded
          ? Yup.string().required("Address is required")
          : Yup.string(),
        cCity: contractorSigning?.notarizationNeeded
          ? Yup.string().required("City is required")
          : Yup.string(),
        cZip: contractorSigning?.notarizationNeeded
          ? Yup.string()
              .required("Zip Code is required")
              .test("zipCode", "Invalid zip code", function (value) {
                return validator.isPostalCode(value, "US");
              })
          : Yup.string(),
        cEmail:
          contractorSigning?.notarizationNeeded ||
          contractorSigning?.signingNeeded
            ? Yup.string()
                .required("Email is required")
                .email("Must be a valid email")
            : Yup.string(),
        oEmail:
          ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded
            ? Yup.string()
                .required("Email is required")
                .email("Must be a valid email")
            : Yup.string(),

        cDOB: Yup.string(),
        cMobile:
          contractorSigning?.notarizationNeeded ||
          contractorSigning?.signingNeeded
            ? Yup.string()
                .required("Mobile number is required")
                .test(
                  "PhoneTest",
                  "Mobile number must be in format XXX-XXX-XXXX",
                  function (value) {
                    if (!value) return false;
                    // Check for exact format XXX-XXX-XXXX
                    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
                    return phoneRegex.test(value);
                  },
                )
            : Yup.string(),
        oMobile:
          ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded
            ? Yup.string()
                .required("Mobile number is required")
                .test(
                  "PhoneTest",
                  "Mobile number must be in format XXX-XXX-XXXX",
                  function (value) {
                    if (!value) return false;
                    // Check for exact format XXX-XXX-XXXX
                    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
                    return phoneRegex.test(value);
                  },
                )
            : Yup.string(),
        oMobileConsent:
          ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded
            ? Yup.boolean().oneOf(
                [true],
                "You must confirm that you have obtained the homeowner's permission to send text messages to the mobile phone number provided above.",
              )
            : Yup.boolean(),
        cIdType: Yup.string(),
      }),
    [ownerSigning, contractorSigning],
  );

  const nocDocument = selectedRow?.documents?.noc;
  const ownerEmailFieldId = defaultFormFields.find(
    (field) => getOwnerDetailFieldKind(field) === "email",
  )?.field_id;
  const ownerPhoneFieldId = defaultFormFields.find(
    (field) => getOwnerDetailFieldKind(field) === "phone",
  )?.field_id;
  const ownerNameFromDocument =
    getDocumentFieldValueByName(nocDocument, "owner", "") ||
    getDocumentFieldValueByName(nocDocument, "ownerName", "") ||
    getDocumentFieldValueByName(nocDocument, "owner_name", "");
  const ownerEmailFromDocument =
    getSharedFieldValueFromDocuments({
      documents: selectedRow?.documents,
      fieldId: ownerEmailFieldId,
    }) ||
    getDocumentFieldValueByName(nocDocument, "ownerEmail", "") ||
    getDocumentFieldValueByName(nocDocument, "owner_email", "");
  const ownerPhoneFromDocument =
    getSharedFieldValueFromDocuments({
      documents: selectedRow?.documents,
      fieldId: ownerPhoneFieldId,
    }) ||
    getDocumentFieldValueByName(nocDocument, "ownerPhone", "") ||
    getDocumentFieldValueByName(nocDocument, "owner_phone", "") ||
    getDocumentFieldValueByName(nocDocument, "ownership_phone", "");

  const ownerFullName =
    ownerSigning.notarizationNeeded || ownerSigning.signingNeeded
      ? ownerNameFromDocument ||
        selectedRow?.property_owner?.personalName ||
        selectedRow?.property_owner?.name ||
        ""
      : ownerNameFromDocument ||
        selectedRow?.property_owner?.personalName ||
        selectedRow?.property_owner?.name ||
        "";
  const ownerNameParts = splitFullName(ownerFullName);

  // Get current contractor data for form initialization
  const getCurrentContractorData = () => {
    if (isStJohnsCounty() && requiredContractors.length > 0) {
      return requiredContractors[currentContractorIndex] || {};
    }
    return selectedRow?.contractor || {};
  };

  const currentContractorData = getCurrentContractorData();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      cFirst: selectedRow?.contractor?.firstName || "",
      cMiddle: selectedRow?.contractor?.middleName || "",
      cLast: selectedRow?.contractor?.lastName || "",
      cAddress: selectedRow?.contractor?.address1 || "",
      cCity: selectedRow?.contractor?.city || "",
      cState: selectedRow?.contractor?.state || "",
      cZip: selectedRow?.contractor?.zip || "",
      cDOB: "",
      cEmail: selectedRow?.contractor?.email || "",
      cMobile: selectedRow?.contractor?.mobile || "",
      cIdType: selectedRow?.contractor?.idType || "",
      oFirst: ownerNameParts.first || "",
      oMiddle: ownerNameParts.middle || "",
      oLast: ownerNameParts.last || "",
      oAddress:
        selectedRow?.property_owner?.address1 ||
        selectedRow?.property_owner?.address_parts?.address1 ||
        selectedRow?.property_owner?.address_parts?.address ||
        "",
      oCity:
        selectedRow?.property_owner?.city ||
        selectedRow?.property_owner?.address_parts?.city ||
        "",
      oZip:
        selectedRow?.property_owner?.zip ||
        selectedRow?.property_owner?.address_parts?.zip_code ||
        selectedRow?.property_owner?.address_parts?.zipCode ||
        "",
      oName: ownerFullName || "",
      oEmail:
        ownerEmailFromDocument || selectedRow?.property_owner?.email || "",
      oMobile:
        ownerPhoneFromDocument || selectedRow?.property_owner?.mobile || "",
      oMobileConsent: false,
    },
    validationSchema,

    onSubmit: async (values, helpers) => {
      try {
        setNotaryPaymentLoading(true);
        setSubmitError("");
        const payload = {
          ...values,
          oName: [values.oFirst, values.oMiddle, values.oLast]
            .filter(Boolean)
            .join(" "),
          cDOB: values.cDOB ? convertDayjsToObject(values.cDOB) : "",
          cBusinessName:
            currentContractorData?.businessName ||
            selectedRow?.contractor?.businessName,
          primaryAddress: selectedRow?.project_address,
          autoStartRecording: autoStartRecording,
          isAcknowledged: isAcknowledged,
          signingOrder: signingOrder,
          paymentCard: paymentCards[activeStep],
          saveCityZip: "",
          cLicense:
            currentContractorData?.licenseNumber ||
            selectedRow?.contractor?.licenseNumber,
          onlyOwner:
            !contractorSigning?.notarizationNeeded &&
            !contractorSigning?.signingNeeded,
          // IPEN & Notary Pool settings
          notarization_type: notarizationType,
          notary_source: getContractorRonNotarySource(),
          owner_ron_notary_source: ownerRonNotarySource,
          contractor_ron_notary_source: getContractorRonNotarySource(),
          ipen_pin: ipenPin,
          owner_notarization_type: ownerNotarizationType,
          owner_require_ipen_pin: ownerRequireIpenPin,
          // For St. Johns: send contractorSettings object, for others: send single values
          contractor_notarization_type: contractorNotarizationType,
          contractor_require_ipen_pin: getContractorRequireIpenPin(),
          owner_ipen_notary_id: ownerIpenNotaryId,
          contractor_ipen_notary_id: contractorIpenNotaryId,
          selected_contractors: contractorSettings,
          plan_review_service: planReviewService,
          inspection_service: inspectionService,
        };

        if (payload["onlyOwner"]) {
          payload["signingOrder"] = "owner";
        }

        await validateSelectedIpenNotaries(payload);

        // Enterprise / Branch users have $0 tiers, so they should never be
        // forced to set up a payment card. The backend also validates that the
        // Stripe price for their tier is $0 before allowing this.
        if (isOrgUser && !isRemoteConnectSession && !isAdminUser) {
          const finalResp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/finish-notary-payment`,
            {
              task_id: selectedRow?.task_id,
              currentSession: currentSession,
              payload,
              documents_type: docNotaryTypeValue,
            },
            { withCredentials: true },
          );

          handlePostPayment(finalResp, helpers);
          return;
        }

        if (isRemoteConnectSession && !isAdminUser) {
          const shouldSkipPayment =
            remotePaymentChoice === "skip" || paymentCards.length === 0;

          if (shouldSkipPayment) {
            const finalResp = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/finish-notary-payment`,
              {
                task_id: selectedRow?.task_id,
                currentSession: currentSession,
                payload,
                documents_type: docNotaryTypeValue,
              },
              { withCredentials: true },
            );

            handlePostPayment(finalResp, helpers);
            return;
          }

          const chargeCard = defaultPaymentCard;
          if (!chargeCard?.id) {
            setSubmitError(
              "No default payment card found. Choose skip payment or add a card.",
            );
            return;
          }

          const resp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/process-notary-payment`,
            {
              paymentCard: chargeCard.id,
              task_id: selectedRow?.task_id,
              acknowledged: isAcknowledged,
              currentSession: currentSession,
              documents_type: docNotaryTypeValue,
            },
            { withCredentials: true },
          );

          if (resp.data.pricing) {
            setConfirmedPricing(resp.data.pricing);
          }

          const stripe = await loadStripe(process.env.REACT_APP_STRIPE);

          if (
            resp.data.payment_status === "succeeded" &&
            resp.data.valid === true
          ) {
            const paymentIntent = {
              id: resp.data.payment_intent_id || resp.data.setup_intent_id,
              status: "succeeded",
            };
            const finalResp = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/finish-notary-payment`,
              {
                paymentIntent,
                task_id: selectedRow?.task_id,
                currentSession: currentSession,
                payload,
                documents_type: docNotaryTypeValue,
              },
              { withCredentials: true },
            );

            handlePostPayment(finalResp, helpers);
            return;
          }

          let result = null;

          try {
            if (resp.data.flow === "setup_intent") {
              result = await stripe.confirmCardSetup(resp.data.data);
            } else {
              result = await stripe.confirmCardPayment(resp.data.data);
            }

            if (result.error) {
              setSubmitError(
                result.error.message ||
                  "Payment failed. Please try again or use a different payment method.",
              );
              setNotaryPaymentLoading(false);
              return;
            }

            let status = null;
            let paymentIntent = null;

            if (resp.data.flow === "setup_intent") {
              status = result.setupIntent.status;
              paymentIntent = result.setupIntent;
            } else {
              status = result.paymentIntent.status;
              paymentIntent = result.paymentIntent;
            }

            if (status === "succeeded") {
              const finalResp = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/finish-notary-payment`,
                {
                  paymentIntent,
                  task_id: selectedRow?.task_id,
                  currentSession: currentSession,
                  currentPath: window.location.pathname,
                  payload,
                  documents_type: docNotaryTypeValue,
                },
                { withCredentials: true },
              );

              handlePostPayment(finalResp, helpers);
            } else {
              setSubmitError(
                `Payment status: ${status}. Please try again or contact support.`,
              );
              setNotaryPaymentLoading(false);
            }
          } catch (error) {
            console.error("Payment confirmation error:", error);
            setSubmitError(
              error.message ||
                "An error occurred during payment confirmation. Please try again.",
            );
            setNotaryPaymentLoading(false);
          }
        } else if (!isAdminUser && !isCustomerPackagesAdminFlow) {
          // Process payment only if not admin / customer-packages admin-style flow
          const resp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/process-notary-payment`,
            {
              paymentCard: paymentCards[activeStep]?.id,
              task_id: selectedRow?.task_id,
              acknowledged: isAcknowledged,
              currentSession: currentSession,
              currentPath: window.location.pathname,
              documents_type: docNotaryTypeValue,
            },
            { withCredentials: true },
          );

          // Store the confirmed pricing from backend
          if (resp.data.pricing) {
            setConfirmedPricing(resp.data.pricing);
          }

          const stripe = await loadStripe(process.env.REACT_APP_STRIPE);

          // Check if payment requires action (3D Secure, etc.)
          // If payment_status is already "succeeded", we can skip confirmation
          if (
            resp.data.payment_status === "succeeded" &&
            resp.data.valid === true
          ) {
            // Payment already succeeded, proceed directly
            const paymentIntent = {
              id: resp.data.payment_intent_id || resp.data.setup_intent_id,
              status: "succeeded",
            };
            const finalResp = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/finish-notary-payment`,
              {
                paymentIntent,
                task_id: selectedRow?.task_id,
                currentSession: currentSession,
                currentPath: window.location.pathname,
                payload,
                documents_type: docNotaryTypeValue,
              },
              { withCredentials: true },
            );

            handlePostPayment(finalResp, helpers);
            return;
          }

          // Payment requires confirmation (3D Secure, etc.)
          // Stripe's confirmCardPayment/confirmCardSetup will automatically
          // show the 3D Secure modal if needed
          let result = null;

          try {
            if (resp.data.flow === "setup_intent") {
              result = await stripe.confirmCardSetup(resp.data.data);
            } else {
              result = await stripe.confirmCardPayment(resp.data.data);
            }

            if (result.error) {
              // Payment failed or was cancelled
              setSubmitError(
                result.error.message ||
                  "Payment failed. Please try again or use a different payment method.",
              );
              setNotaryPaymentLoading(false);
              return;
            }

            // Check the payment status after confirmation
            let status = null;
            let paymentIntent = null;

            if (resp.data.flow === "setup_intent") {
              status = result.setupIntent.status;
              paymentIntent = result.setupIntent;
            } else {
              status = result.paymentIntent.status;
              paymentIntent = result.paymentIntent;
            }

            // Only proceed if payment succeeded
            if (status === "succeeded") {
              const finalResp = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/finish-notary-payment`,
                {
                  paymentIntent,
                  task_id: selectedRow?.task_id,
                  currentSession: currentSession,
                  currentPath: window.location.pathname,
                  payload,
                  documents_type: docNotaryTypeValue,
                },
                { withCredentials: true },
              );

              handlePostPayment(finalResp, helpers);
            } else {
              // Payment not succeeded (shouldn't happen after confirmation, but handle it)
              setSubmitError(
                `Payment status: ${status}. Please try again or contact support.`,
              );
              setNotaryPaymentLoading(false);
            }
          } catch (error) {
            // Handle any errors during confirmation
            console.error("Payment confirmation error:", error);
            setSubmitError(
              error.message ||
                "An error occurred during payment confirmation. Please try again.",
            );
            setNotaryPaymentLoading(false);
          }
        } else {
          // If admin or customer-packages admin-style flow, skip payment but still call finish-notary-payment
          const finalResp = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/stripe/finish-notary-payment`,
            {
              task_id: selectedRow?.task_id,
              currentSession: currentSession,
              currentPath: window.location.pathname,
              payload,
              documents_type: docNotaryTypeValue,
            },
            { withCredentials: true },
          );

          handlePostPayment(finalResp, helpers);
        }
      } catch (error) {
        console.error(error);
        setSubmitError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to submit notarization request.",
        );
      } finally {
        setOpenConfirmationModal(false);
        setNotaryPaymentLoading(false);
      }
    },
  });

  const EMAIL_CONFLICT_MESSAGE =
    "Owner/Agent and Contractor emails must be different.";

  const isEmailConflict = useMemo(() => {
    const ownerRequired =
      ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded;
    const contractorRequired =
      contractorSigning?.notarizationNeeded || contractorSigning?.signingNeeded;

    if (!ownerRequired || !contractorRequired) {
      return false;
    }

    const ownerEmail = (formik.values.oEmail || "").trim().toLowerCase();
    const contractorEmail = (formik.values.cEmail || "").trim().toLowerCase();

    if (!ownerEmail || !contractorEmail) {
      return false;
    }

    return ownerEmail === contractorEmail;
  }, [
    ownerSigning,
    contractorSigning,
    formik.values.oEmail,
    formik.values.cEmail,
  ]);

  useEffect(() => {
    const isInformationStep =
      wizardSteps[currentWizardStep]?.id === "information";

    if (isEmailConflict) {
      if (formik.errors.oEmail !== EMAIL_CONFLICT_MESSAGE) {
        formik.setFieldError("oEmail", EMAIL_CONFLICT_MESSAGE);
      }
      if (formik.errors.cEmail !== EMAIL_CONFLICT_MESSAGE) {
        formik.setFieldError("cEmail", EMAIL_CONFLICT_MESSAGE);
      }

      if (isInformationStep) {
        if (!formik.touched.oEmail) {
          formik.setFieldTouched("oEmail", true, false);
        }
        if (!formik.touched.cEmail) {
          formik.setFieldTouched("cEmail", true, false);
        }
      }

      return;
    }

    if (formik.errors.oEmail === EMAIL_CONFLICT_MESSAGE) {
      formik.setFieldError("oEmail", undefined);
    }
    if (formik.errors.cEmail === EMAIL_CONFLICT_MESSAGE) {
      formik.setFieldError("cEmail", undefined);
    }
  }, [
    isEmailConflict,
    wizardSteps,
    currentWizardStep,
    formik.errors.oEmail,
    formik.errors.cEmail,
    formik.touched.oEmail,
    formik.touched.cEmail,
    EMAIL_CONFLICT_MESSAGE,
    formik,
  ]);

  // Auto-fill owner information when ownerSigning becomes required (e.g., pp_ntbo with feeOwner)
  useEffect(() => {
    if (
      (ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded) &&
      selectedRow?.property_owner
    ) {
      // Only update if fields are empty to avoid overwriting user input
      // Prefill owner name fields if empty
      if (!formik.values.oFirst || !formik.values.oLast) {
        const ownerName =
          ownerNameFromDocument ||
          selectedRow.property_owner.personalName ||
          selectedRow.property_owner.name ||
          "";
        if (ownerName) {
          const nameParts = splitFullName(ownerName);
          if (!formik.values.oFirst) {
            formik.setFieldValue("oFirst", nameParts.first);
          }
          if (!formik.values.oMiddle) {
            formik.setFieldValue("oMiddle", nameParts.middle);
          }
          if (!formik.values.oLast) {
            formik.setFieldValue("oLast", nameParts.last);
          }
          // Also set oName for backward compatibility
          if (!formik.values.oName) {
            formik.setFieldValue("oName", ownerName);
          }
        }
      }
      // Prefill owner address fields if empty
      if (ownerSigning?.notarizationNeeded) {
        const addressParts = selectedRow.property_owner?.address_parts || {};
        if (!formik.values.oAddress && addressParts.address1) {
          formik.setFieldValue("oAddress", addressParts.address1);
        }
        if (!formik.values.oCity && addressParts.city) {
          formik.setFieldValue("oCity", addressParts.city);
        }
        if (!formik.values.oZip && addressParts.zip_code) {
          formik.setFieldValue("oZip", addressParts.zip_code);
        }
      }
      if (!formik.values.oEmail) {
        const ownerEmail =
          ownerEmailFromDocument || selectedRow.property_owner.email || "";
        if (ownerEmail) {
          formik.setFieldValue("oEmail", ownerEmail);
        }
      }
      if (!formik.values.oMobile) {
        const ownerMobile =
          ownerPhoneFromDocument || selectedRow.property_owner.mobile || "";
        if (ownerMobile) {
          formik.setFieldValue("oMobile", ownerMobile);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ownerSigning?.notarizationNeeded,
    ownerSigning?.signingNeeded,
    selectedRow?.property_owner,
  ]);

  // Validation function for Signer Information step
  const isSignerInformationValid = useCallback(() => {
    if (isEmailConflict) {
      return false;
    }

    // Check owner/agent fields (always shown)
    if (ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded) {
      if (
        !formik.values.oFirst ||
        !formik.values.oLast ||
        !formik.values.oEmail ||
        !formik.values.oMobile
      ) {
        return false;
      }
      if (
        !OWNER_NAME_REGEX.test(formik.values.oFirst) ||
        !OWNER_NAME_REGEX.test(formik.values.oLast) ||
        (formik.values.oMiddle && !OWNER_NAME_REGEX.test(formik.values.oMiddle))
      ) {
        return false;
      }
      if (
        hasInvalidOwnerBusinessNameValue(formik.values.oFirst) ||
        hasInvalidOwnerBusinessNameValue(formik.values.oMiddle) ||
        hasInvalidOwnerBusinessNameValue(formik.values.oLast)
      ) {
        return false;
      }
      // Check address fields if notarization is needed
      if (
        ownerSigning?.notarizationNeeded &&
        (!formik.values.oAddress || !formik.values.oCity || !formik.values.oZip)
      ) {
        return false;
      }
      // Check if mobile consent checkbox is checked
      if (!formik.values.oMobileConsent) {
        return false;
      }
    }

    // Check contractor fields (if contractor needs to sign/notarize)
    if (
      contractorSigning?.notarizationNeeded ||
      contractorSigning?.signingNeeded
    ) {
      if (!formik.values.cEmail || !formik.values.cMobile) {
        return false;
      }
      // If notarization needed, also check address fields
      if (contractorSigning?.notarizationNeeded) {
        if (
          !formik.values.cAddress ||
          !formik.values.cCity ||
          !formik.values.cZip
        ) {
          return false;
        }
      }
    }

    return true;
  }, [formik.values, ownerSigning, contractorSigning, isEmailConflict]);

  const ownerInfoRequired =
    ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded;

  const handleOwnerNameChange = (event) => {
    formik.handleChange(event);
    formik.setFieldTouched(event.target.name, true, false);
  };

  useEffect(() => {
    const ownerNameFields = ["oFirst", "oMiddle", "oLast"];
    const hasBusinessNameError = ownerNameFields.some(
      (fieldName) =>
        formik.errors[fieldName] === OWNER_BUSINESS_NAME_ERROR_MESSAGE,
    );

    const fieldWithBusinessKeyword = ownerNameFields.find((fieldName) =>
      hasInvalidOwnerBusinessNameValue(formik.values[fieldName]),
    );

    if (fieldWithBusinessKeyword) {
      if (
        formik.errors[fieldWithBusinessKeyword] !==
        OWNER_BUSINESS_NAME_ERROR_MESSAGE
      ) {
        formik.setFieldError(
          fieldWithBusinessKeyword,
          OWNER_BUSINESS_NAME_ERROR_MESSAGE,
        );
      }
      return;
    }

    if (hasBusinessNameError) {
      ownerNameFields.forEach((fieldName) => {
        if (formik.errors[fieldName] === OWNER_BUSINESS_NAME_ERROR_MESSAGE) {
          formik.setFieldError(fieldName, undefined);
        }
      });
    }
  }, [
    formik.values.oFirst,
    formik.values.oMiddle,
    formik.values.oLast,
    formik.errors,
    formik,
  ]);

  // Individual Step Components
  const renderOverviewStep = () => {
    return (
      <AnimatedStep>
        <Typography
          variant="h6"
          align="center"
          color="primary"
          fontWeight="bold"
          mb={2}
        >
          Document Overview & Requirements
        </Typography>

        <Alert severity="info" sx={{ mb: 2, py: 0.5, px: mobileSize ? 1 : 2 }}>
          <Typography variant="body2">{getInstructionText()}</Typography>
        </Alert>

        {/* Project Info */}
        <Card
          sx={{
            mb: 2,
            p: mobileSize ? 1.5 : 2,
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          <Grid container spacing={mobileSize ? 1 : 2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="caption"
                sx={{ opacity: 0.9, fontSize: "0.7rem" }}
              >
                PROJECT NAME
              </Typography>
              <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                {selectedRow?.package_name || "N/A"}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="caption"
                sx={{ opacity: 0.9, fontSize: "0.7rem" }}
              >
                JOB LOCATION
              </Typography>
              <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                {selectedRow?.project_address || "N/A"}
              </Typography>
            </Grid>
          </Grid>
        </Card>

        {/* Documents Requirements Table */}
        <Card sx={{ mb: 0.5 }}>
          <Box sx={{ p: 1.5, pb: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="primary"
              gutterBottom
            >
              📋 Selected Documents & Requirements
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Review which documents require signatures and notarization from
              each party
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 270 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "primary.main",
                      color: "white",
                      py: 1,
                    }}
                  >
                    {mobileSize ? "Documents" : "Document"}
                  </TableCell>
                  {!mobileSize && (
                    <>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          bgcolor: "primary.main",
                          color: "white",
                          py: 1,
                        }}
                        align="center"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="inherit">Owner</Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleDocumentParty("owner")}
                            disabled={
                              signerOverrideToggleLocks.owner ||
                              !ownerHasToggleableRequirements ||
                              Boolean(partyToggleLoading) ||
                              notaryPaymentLoading
                            }
                            sx={{
                              color: ownerPartyEnabled
                                ? "#0d47a1"
                                : "#00000066",
                              p: 0.25,
                              "&.Mui-disabled": {
                                color: "rgba(13, 71, 161, 0.45)",
                              },
                            }}
                          >
                            {ownerPartyEnabled ? (
                              <ToggleOnIcon fontSize="small" />
                            ) : (
                              <ToggleOffIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          bgcolor: "primary.main",
                          color: "white",
                          py: 1,
                        }}
                        align="center"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="inherit">Contractor</Typography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleToggleDocumentParty("contractor")
                            }
                            disabled={
                              signerOverrideToggleLocks.contractor ||
                              !contractorHasToggleableRequirements ||
                              Boolean(partyToggleLoading) ||
                              notaryPaymentLoading
                            }
                            sx={{
                              color: contractorPartyEnabled
                                ? "#0d47a1"
                                : "#00000066",
                              p: 0.25,
                              "&.Mui-disabled": {
                                color: "rgba(13, 71, 161, 0.45)",
                              },
                            }}
                          >
                            {contractorPartyEnabled ? (
                              <ToggleOnIcon fontSize="small" />
                            ) : (
                              <ToggleOffIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          bgcolor: "primary.main",
                          color: "white",
                          py: 1,
                        }}
                        align="center"
                      >
                        Method
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {deferredFilteredDocs.map((doc, index) => {
                  const {
                    ownerSign,
                    ownerNotary,
                    contractorSign,
                    contractorNotary,
                  } = getDocumentSigningRequirements(doc);

                  return (
                    <TableRow key={index} hover>
                      {mobileSize ? (
                        <TableCell sx={{ padding: "8px !important" }}>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            mb={0.75}
                          >
                            {getDocumentDisplayLabel(doc, index)}
                          </Typography>
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            columnGap={2}
                            rowGap={0.5}
                            flexWrap={"wrap"}
                            mb={0.5}
                          >
                            <Box display={"flex"} alignItems={"center"} gap={1}>
                              <Typography
                                variant="caption"
                                sx={{ opacity: 0.9, fontSize: "0.7rem" }}
                              >
                                Owner
                              </Typography>
                              {renderPartyRequirementChip(doc, "owner")}
                            </Box>
                            <Box display={"flex"} alignItems={"center"} gap={1}>
                              <Typography
                                variant="caption"
                                sx={{ opacity: 0.9, fontSize: "0.7rem" }}
                              >
                                Contractor
                              </Typography>
                              {renderPartyRequirementChip(doc, "contractor")}
                            </Box>
                          </Box>
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            gap={1}
                            mb={0.5}
                          >
                            <Typography
                              variant="caption"
                              sx={{ opacity: 0.9, fontSize: "0.7rem" }}
                            >
                              Method
                            </Typography>
                            {ownerNotary || contractorNotary ? (
                              <Chip
                                label="RON/IPEN"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem", height: 20 }}
                              />
                            ) : ownerSign || contractorSign ? (
                              <Chip
                                label="E-Sign"
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem", height: 20 }}
                              />
                            ) : (
                              <Chip
                                label="No Action"
                                size="small"
                                color="default"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem", height: 20 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      ) : (
                        <>
                          <TableCell sx={{ py: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {getDocumentDisplayLabel(doc, index)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1 }}>
                            {renderPartyRequirementChip(doc, "owner")}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1 }}>
                            {renderPartyRequirementChip(doc, "contractor")}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1 }}>
                            {ownerNotary || contractorNotary ? (
                              <Chip
                                label="RON/IPEN"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem", height: 20 }}
                              />
                            ) : ownerSign || contractorSign ? (
                              <Chip
                                label="E-Sign"
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem", height: 20 }}
                              />
                            ) : (
                              <Chip
                                label="No Action"
                                size="small"
                                color="default"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem", height: 20 }}
                              />
                            )}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {selectedRow?.assigned_package?.need_assign && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {signingOrder === "contractor"
                ? "If the contractor is selected as the first signer, the homeowner cannot begin their part of the signing process until the contractor completes theirs."
                : ""}{" "}
              The documents will be automatically forwarded to the contractor as
              soon as the process starts.
            </Typography>
          </Alert>
        )}
      </AnimatedStep>
    );
  };

  const renderMethodStep = () => (
    <AnimatedStep>
      <Typography
        variant="h6"
        align="center"
        color="primary"
        fontWeight="bold"
        mb={2}
      >
        Notarization Method
      </Typography>

      {contractorSigning?.notarizationNeeded ||
      ownerSigning?.notarizationNeeded ? (
        <Stack spacing={3}>
          <FormControl
            component="fieldset"
            disabled={notaryPaymentLoading}
            sx={{ width: "100%" }}
          >
            <RadioGroup
              value={notarizationType}
              onChange={(e) => setNotarizationType(e.target.value)}
              sx={{ gap: 2 }}
            >
              {/* RON Option */}
              <StepCard active={notarizationType === "ron"}>
                <CardContent>
                  <FormControlLabel
                    value="ron"
                    control={<Radio />}
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                          Remote Online Notarization (RON)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Notarization completed online via video call
                        </Typography>
                      </Box>
                    }
                    sx={{ width: "100%", alignItems: "flex-start" }}
                  />

                  {/* RON Notary Source Selection */}
                  {notarizationType === "ron" && (
                    <Box sx={{ ml: 6, mt: 2 }}>
                      <FormLabel
                        component="legend"
                        sx={{
                          fontSize: "0.9rem",
                          mb: 1,
                          color: "text.primary",
                        }}
                      >
                        Select Notary Provider
                      </FormLabel>
                      <RadioGroup
                        value={getContractorRonNotarySource()}
                        onChange={(e) =>
                          setContractorRonNotarySource(e.target.value)
                        }
                      >
                        <FormControlLabel
                          value="permitRockstar"
                          control={<Radio size="small" />}
                          label="Use Permit Rockstar notary"
                        />
                        {hasRonOrgNotaries && (
                          <FormControlLabel
                            value="organization"
                            control={<Radio size="small" />}
                            label={`Use ${organizationName} notaries`}
                          />
                        )}
                      </RadioGroup>
                    </Box>
                  )}
                </CardContent>
              </StepCard>

              {/* IPEN Option */}
              <StepCard active={notarizationType === "ipen"}>
                <CardContent>
                  <FormControlLabel
                    control={<Radio />}
                    disabled={!canUseIpen}
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            component="span"
                          >
                            In-Person Electronic Notarization (IPEN)
                          </Typography>
                          {!hasOrgNotaries && (
                            <Chip
                              label="Requires org notary"
                              size="small"
                              color="warning"
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Notarization completed in person with your
                          organization's notary
                        </Typography>
                      </Box>
                    }
                    sx={{ width: "100%", alignItems: "flex-start" }}
                  />
                </CardContent>
              </StepCard>
            </RadioGroup>
          </FormControl>

          {/* IPEN PIN Display */}
          {notarizationType === "ipen" && ipenPin && (
            <Card
              sx={{ p: 2, bgcolor: "#e3f2fd", border: "2px solid #2196f3" }}
            >
              <Typography
                variant="subtitle1"
                color="primary"
                fontWeight="bold"
                align="center"
                mb={1}
              >
                📌 Verification PIN
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: "monospace",
                  letterSpacing: 4,
                  textAlign: "center",
                  color: "#1976d2",
                  fontWeight: "bold",
                  mb: 1,
                }}
              >
                {ipenPin}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Provide this PIN to your notary to verify physical presence
              </Typography>
            </Card>
          )}

          {/* Mixed Session Warning */}
          {notarizationType === "ipen" &&
            ownerSigning.notarizationNeeded &&
            contractorSigning.notarizationNeeded && (
              <Alert severity="info">
                <Typography variant="body2">
                  ℹ️ <strong>Note:</strong> Since both owner and contractor
                  notarization is required, contractor documents will use IPEN
                  while owner documents will be processed separately via RON.
                </Typography>
              </Alert>
            )}

          {/* IPEN Not Available Message */}
          {!canUseIpen && !hasOrgNotaries && (
            <Alert severity="warning">
              <Typography variant="body2">
                💡 To enable IPEN, add notary-certified users to your
                organization
              </Typography>
            </Alert>
          )}
        </Stack>
      ) : (
        <Card sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            No notarization required for these documents.
          </Typography>
        </Card>
      )}
    </AnimatedStep>
  );

  const renderNotaryStep = () => (
    <AnimatedStep>
      <Typography
        variant="h6"
        align="center"
        color="primary"
        fontWeight="bold"
        mb={2}
      >
        Notary Selection
      </Typography>

      {notarizationType === "ipen" && hasOrgNotaries ? (
        <Card
          sx={{
            p: 3,
            background: "linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)",
            border: "1px solid #e3f2fd",
            borderRadius: "16px",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography variant="h6" color="primary" fontWeight="bold" mb={1}>
              🏢 Organization Notary Selection
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a certified notary from your organization for in-person
              notarization
            </Typography>
          </Box>

          {loadingNotaries ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                🔍 Loading organization notaries...
              </Typography>
            </Box>
          ) : availableNotaries.length > 0 ? (
            <RadioGroup
              value={selectedNotary?.notary_id || ""}
              onChange={(e) => {
                const notary = availableNotaries.find(
                  (n) => n.notary_id === e.target.value,
                );
                setSelectedNotary(notary);
              }}
              sx={{ gap: 2 }}
            >
              {availableNotaries.map((notary) => (
                <StepCard
                  key={notary.notary_id}
                  active={selectedNotary?.notary_id === notary.notary_id}
                  sx={{
                    borderRadius: "12px",
                    border:
                      selectedNotary?.notary_id === notary.notary_id
                        ? "2px solid #1976d2"
                        : "2px solid #e0e0e0",
                    background:
                      selectedNotary?.notary_id === notary.notary_id
                        ? "linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)"
                        : "white",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 25px rgba(25, 118, 210, 0.15)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <FormControlLabel
                      value={notary.notary_id}
                      control={
                        <Radio
                          sx={{
                            color:
                              selectedNotary?.notary_id === notary.notary_id
                                ? "#1976d2"
                                : "#9e9e9e",
                            "&.Mui-checked": {
                              color: "#1976d2",
                            },
                          }}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            ml: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              background:
                                "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontWeight: "bold",
                              fontSize: "1.2rem",
                            }}
                          >
                            {notary.firstName?.[0]}
                            {notary.lastName?.[0]}
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              color="primary"
                            >
                              {notary.firstName} {notary.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {notary.email}
                            </Typography>
                            <Chip
                              label="Certified Notary"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mt: 0.5, fontSize: "0.7rem" }}
                            />
                          </Box>
                        </Box>
                      }
                      sx={{ width: "100%", alignItems: "flex-start", m: 0 }}
                    />
                  </CardContent>
                </StepCard>
              ))}
            </RadioGroup>
          ) : (
            <Alert
              severity="warning"
              sx={{
                borderRadius: "12px",
                background: "linear-gradient(135deg, #fff3e0 0%, #ffffff 100%)",
                border: "1px solid #ffb74d",
              }}
            >
              <Typography variant="body2" fontWeight="medium">
                ⚠️ No available notaries found in your organization. Please
                contact your administrator to add certified notaries.
              </Typography>
            </Alert>
          )}
        </Card>
      ) : notarizationType === "ipen" && !hasOrgNotaries ? (
        <Card
          sx={{
            p: 3,
            textAlign: "center",
            background: "linear-gradient(135deg, #fff3e0 0%, #ffffff 100%)",
            border: "1px solid #ffb74d",
            borderRadius: "16px",
          }}
        >
          <Typography
            variant="h6"
            color="warning.main"
            fontWeight="bold"
            mb={2}
          >
            🚫 IPEN Not Available
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Your organization doesn't have certified notaries available for
            in-person notarization.
          </Typography>
          <Alert severity="info" sx={{ borderRadius: "12px" }}>
            <Typography variant="body2">
              💡 To enable IPEN, your organization needs to have certified
              notaries. Please contact your administrator or switch to Remote
              Online Notarization (RON).
            </Typography>
          </Alert>
        </Card>
      ) : (
        <Card
          sx={{
            p: 4,
            textAlign: "center",
            background: "linear-gradient(135deg, #e8f5e8 0%, #ffffff 100%)",
            border: "1px solid #4caf50",
            borderRadius: "16px",
          }}
        >
          <Typography
            variant="h6"
            color="success.main"
            fontWeight="bold"
            mb={2}
          >
            ✅ Notary Assignment Complete
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {notarizationType === "ron"
              ? "Your notary will be automatically assigned after payment and scheduling."
              : "No notary selection required for this document type."}
          </Typography>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: "20px",
              background: "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)",
              color: "white",
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              🎯 Ready to Continue
            </Typography>
          </Box>
        </Card>
      )}
    </AnimatedStep>
  );

  const renderServicesStep = () => (
    <AnimatedStep>
      <Typography
        variant="h6"
        align="center"
        color="primary"
        fontWeight="bold"
        mb={1}
      >
        Notarization Method
      </Typography>

      <Typography variant="body2" color="text.secondary" align="center" mb={3}>
        Select notarization method for each party
      </Typography>

      <Grid container spacing={2} sx={{ my: 2 }}>
        {/* Contractor Notarization */}
        {contractorSigning?.notarizationNeeded && (
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{ height: tabletSize ? "100%" : undefined }}
          >
            <Card
              sx={{
                p: mobileSize ? 1.5 : 2.5,
                height: "100%",
                background: "linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%)",
                border: "1px solid #e1f5fe",
                borderRadius: "12px",
                boxShadow: "0 2px 12px rgba(0, 150, 136, 0.08)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      background:
                        "linear-gradient(135deg, #009688 0%, #26a69a 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "1.2rem",
                    }}
                  >
                    🔧
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="primary"
                    >
                      Contractor
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isStJohnsCounty() && requiredContractors.length > 0
                        ? requiredContractors[currentContractorIndex]
                            ?.businessName ||
                          requiredContractors[currentContractorIndex]
                            ?.dba_name ||
                          "Choose notarization method"
                        : selectedRow?.contractor?.businessName ||
                          selectedRow?.contractor?.dba_name ||
                          "Choose notarization method"}
                    </Typography>
                  </Box>
                </Box>
                {isStJohnsCounty() && requiredContractors.length > 1 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        // Save current contractor's form values before switching (only for Signer Information step)

                        const prevIndex =
                          currentContractorIndex > 0
                            ? currentContractorIndex - 1
                            : requiredContractors.length - 1;
                        // Mark previous contractor as seen when navigating back
                        setContractorsSeen((prev) => {
                          const newSet = new Set(prev);
                          newSet.add(prevIndex);
                          return newSet;
                        });
                        setCurrentContractorIndex(prevIndex);
                      }}
                      sx={{
                        color: "primary.main",
                        "&:hover": {
                          backgroundColor: "rgba(0, 150, 136, 0.1)",
                        },
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: "40px", textAlign: "center" }}
                    >
                      {currentContractorIndex + 1} /{" "}
                      {requiredContractors.length}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const nextIndex =
                          currentContractorIndex <
                          requiredContractors.length - 1
                            ? currentContractorIndex + 1
                            : 0;
                        // Mark current and next contractor as seen
                        setContractorsSeen((prev) => {
                          const newSet = new Set(prev);
                          newSet.add(currentContractorIndex);
                          newSet.add(nextIndex);
                          // Clear error if all contractors are now seen
                          if (newSet.size >= requiredContractors.length) {
                            setSubmitError("");
                          }
                          return newSet;
                        });
                        setCurrentContractorIndex(nextIndex);
                      }}
                      sx={{
                        color:
                          submitError &&
                          requiredContractors.length > 1 &&
                          contractorsSeen.size < requiredContractors.length
                            ? "error.main"
                            : "primary.main",
                        "&:hover": {
                          backgroundColor:
                            submitError &&
                            requiredContractors.length > 1 &&
                            contractorsSeen.size < requiredContractors.length
                              ? "rgba(211, 47, 47, 0.1)"
                              : "rgba(0, 150, 136, 0.1)",
                        },
                      }}
                    >
                      <ArrowForwardIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>

              <FormControl component="fieldset" sx={{ width: "100%" }}>
                <RadioGroup
                  value={getContractorNotarizationType()}
                  onChange={(e) =>
                    setContractorNotarizationType(e.target.value)
                  }
                  sx={{ gap: 1 }}
                >
                  <FormControlLabel
                    value="ron"
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">
                          RON - Remote Online Notarization
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Notarization completed online via video call
                        </Typography>
                      </Box>
                    }
                    sx={{
                      p: mobileSize ? 0 : 1,
                      borderRadius: "8px",
                      border:
                        getContractorNotarizationType() === "ron"
                          ? "2px solid #009688"
                          : "1px solid #e0e0e0",
                      background:
                        getContractorNotarizationType() === "ron"
                          ? "rgba(0, 150, 136, 0.08)"
                          : "transparent",
                      transition: "all 0.2s ease",
                      m: 0,
                      mb: 0,
                      "&:hover": {
                        background: "rgba(0, 150, 136, 0.04)",
                      },
                    }}
                  />
                  {getContractorNotarizationType() === "ron" && (
                    <Box sx={{ mb: 1, pl: 4 }}>
                      <FormControl fullWidth size="small" variant="filled">
                        <InputLabel id="contractor-ron-source-select-label">
                          Select Notary Provider
                        </InputLabel>
                        <Select
                          labelId="contractor-ron-source-select-label"
                          id="contractor-ron-source-select"
                          value={getContractorRonNotarySource()}
                          label="Select Notary Provider"
                          onChange={(e) =>
                            setContractorRonNotarySource(e.target.value)
                          }
                          disabled={notaryPaymentLoading}
                          sx={{
                            "& .MuiSelect-select": {
                              whiteSpace: "normal !important",
                              wordBreak: "break-word !important",
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                width: "auto",
                                maxWidth: "100%",
                              },
                            },
                          }}
                        >
                          <MenuItem
                            value="permitRockstar"
                            sx={{
                              whiteSpace: "normal", // Allow text to wrap
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            Permit Rockstar Notaries
                          </MenuItem>
                          {hasRonOrgNotaries && (
                            <MenuItem
                              value="organization"
                              sx={{
                                whiteSpace: "normal", // Allow text to wrap
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                              }}
                            >
                              {organizationName} Notaries
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  <FormControlLabel
                    value="ipen"
                    control={<Radio size="small" disabled={!canUseIpen} />}
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">
                          IPEN - In-Person Electronic Notarization
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Notarization completed in person with your
                          organization's notary
                        </Typography>
                      </Box>
                    }
                    sx={{
                      p: mobileSize ? 0 : 1,
                      borderRadius: "8px",
                      border:
                        getContractorNotarizationType() === "ipen"
                          ? "2px solid #009688"
                          : "1px solid #e0e0e0",
                      background:
                        getContractorNotarizationType() === "ipen"
                          ? "rgba(0, 150, 136, 0.08)"
                          : "transparent",
                      transition: "all 0.2s ease",
                      m: 0,
                      opacity: !canUseIpen ? 0.5 : 1,
                      "&:hover": {
                        background: "rgba(0, 150, 136, 0.04)",
                      },
                    }}
                  />
                </RadioGroup>
              </FormControl>

              {/* IPEN Notary Selection for Contractor */}
              {getContractorNotarizationType() === "ipen" && hasOrgNotaries && (
                <Box sx={{ mt: 2, pl: 4 }}>
                  <FormControl
                    fullWidth
                    size="small"
                    variant="filled"
                    error={
                      getContractorNotarizationType() === "ipen" &&
                      hasOrgNotaries &&
                      ((!getContractorIpenNotaryId() &&
                        wizardSteps[currentWizardStep]?.id === "services") ||
                        (getContractorIpenNotaryId() &&
                          commissionErrors[getContractorIpenNotaryId()]))
                    }
                    required
                  >
                    <InputLabel id="contractor-ipen-notary-select-label">
                      Select Notary
                    </InputLabel>
                    <Select
                      labelId="contractor-ipen-notary-select-label"
                      id="contractor-ipen-notary-select"
                      value={getContractorIpenNotaryId()}
                      label="Select Notary"
                      onChange={(e) =>
                        setContractorIpenNotaryId(e.target.value)
                      }
                      disabled={
                        notaryPaymentLoading ||
                        (isStJohnsCounty() &&
                          requiredContractors.length > 1 &&
                          currentContractorIndex !== 0 &&
                          getContractorIpenNotaryId() && // Only disable if notary is already selected
                          // Check if any other contractor has IPEN selected
                          // If no other contractor has IPEN, allow current contractor to select notary
                          requiredContractors.some(
                            (contractor, index) =>
                              index !== currentContractorIndex &&
                              contractorSettings[contractor?.licenseNumber]
                                ?.notarizationType === "ipen",
                          ))
                      }
                      sx={{
                        "& .MuiSelect-select": {
                          whiteSpace: "normal !important",
                          wordBreak: "break-word !important",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                            width: "auto",
                            maxWidth: "100%",
                          },
                        },
                      }}
                    >
                      {orgNotarySubscriptions
                        ?.filter(
                          (sub) =>
                            sub.is_subuser === false || sub.is_subuser === true,
                        )
                        .map((subscription) => (
                          <MenuItem
                            key={subscription.user_id}
                            value={subscription.user_id}
                            sx={{
                              whiteSpace: "normal", // Allow text to wrap
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            {subscription.full_name}
                          </MenuItem>
                        ))}
                    </Select>
                    {getContractorNotarizationType() === "ipen" &&
                      hasOrgNotaries &&
                      wizardSteps[currentWizardStep]?.id === "services" && (
                        <FormHelperText>
                          {!getContractorIpenNotaryId()
                            ? "Please select a notary for IPEN notarization"
                            : commissionErrors[getContractorIpenNotaryId()]}
                        </FormHelperText>
                      )}
                  </FormControl>
                  <FormControl component="fieldset" sx={{ mt: 2 }}>
                    <FormLabel
                      component="legend"
                      sx={{ fontSize: "0.85rem", color: "text.secondary" }}
                    >
                      Require IPEN PIN
                    </FormLabel>
                    <RadioGroup
                      row
                      value={getContractorRequireIpenPin() ? "yes" : "no"}
                      onChange={(e) =>
                        setContractorRequireIpenPinValue(
                          e.target.value === "yes",
                        )
                      }
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio size="small" />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio size="small" />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
            </Card>
          </Grid>
        )}

        {/* Property Owner Notarization */}
        {ownerSigning?.notarizationNeeded && (
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{ height: tabletSize ? "100%" : undefined }}
          >
            <Card
              sx={{
                p: mobileSize ? 1.5 : 2.5,
                height: "100%",
                background: "linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)",
                border: "1px solid #e3f2fd",
                borderRadius: "12px",
                boxShadow: "0 2px 12px rgba(25, 118, 210, 0.08)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background:
                      "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "1.2rem",
                  }}
                >
                  🏠
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="primary"
                  >
                    Property Owner
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Choose notarization method
                  </Typography>
                </Box>
              </Box>

              <FormControl component="fieldset" sx={{ width: "100%" }}>
                <RadioGroup
                  value={ownerNotarizationType}
                  onChange={(e) => setOwnerNotarizationType(e.target.value)}
                  sx={{ gap: 1 }}
                >
                  <FormControlLabel
                    value="ron"
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">
                          RON - Remote Online Notarization
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Notarization completed online via video call
                        </Typography>
                      </Box>
                    }
                    sx={{
                      p: mobileSize ? 0 : 1,
                      borderRadius: "8px",
                      border:
                        ownerNotarizationType === "ron"
                          ? "2px solid #1976d2"
                          : "1px solid #e0e0e0",
                      background:
                        ownerNotarizationType === "ron"
                          ? "rgba(25, 118, 210, 0.08)"
                          : "transparent",
                      transition: "all 0.2s ease",
                      m: 0,
                      mb: 0,
                      "&:hover": {
                        background: "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                  />
                  {ownerNotarizationType === "ron" && (
                    <Box sx={{ mb: 1, pl: 4 }}>
                      <FormControl fullWidth size="small" variant="filled">
                        <InputLabel id="owner-ron-source-select-label">
                          Select Notary Provider
                        </InputLabel>
                        <Select
                          labelId="owner-ron-source-select-label"
                          id="owner-ron-source-select"
                          value={ownerRonNotarySource}
                          label="Select Notary Provider"
                          onChange={(e) =>
                            setOwnerRonNotarySource(e.target.value)
                          }
                          disabled={notaryPaymentLoading}
                          sx={{
                            "& .MuiSelect-select": {
                              whiteSpace: "normal !important",
                              wordBreak: "break-word !important",
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                width: "auto",
                                maxWidth: "100%",
                              },
                            },
                          }}
                        >
                          <MenuItem
                            value="permitRockstar"
                            sx={{
                              whiteSpace: "normal", // Allow text to wrap
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            Permit Rockstar Notaries
                          </MenuItem>
                          {hasRonOrgNotaries && (
                            <MenuItem
                              value="organization"
                              sx={{
                                whiteSpace: "normal", // Allow text to wrap
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                              }}
                            >
                              {organizationName} Notaries
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  <FormControlLabel
                    value="ipen"
                    control={<Radio size="small" disabled={!hasOrgNotaries} />}
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight="medium">
                          IPEN - In-Person Electronic Notarization
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Notarization completed in person with your
                          organization's notary
                        </Typography>
                      </Box>
                    }
                    sx={{
                      p: mobileSize ? 0 : 1,
                      borderRadius: "8px",
                      border:
                        ownerNotarizationType === "ipen"
                          ? "2px solid #1976d2"
                          : "1px solid #e0e0e0",
                      background:
                        ownerNotarizationType === "ipen"
                          ? "rgba(25, 118, 210, 0.08)"
                          : "transparent",
                      transition: "all 0.2s ease",
                      m: 0,
                      opacity: !hasOrgNotaries ? 0.5 : 1,
                      "&:hover": {
                        background: "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                  />
                </RadioGroup>
              </FormControl>

              {/* IPEN Notary Selection for Owner */}
              {ownerNotarizationType === "ipen" && hasOrgNotaries && (
                <Box sx={{ mt: 2, pl: 4 }}>
                  <FormControl
                    fullWidth
                    size="small"
                    variant="filled"
                    error={
                      ownerNotarizationType === "ipen" &&
                      hasOrgNotaries &&
                      ((!ownerIpenNotaryId &&
                        wizardSteps[currentWizardStep]?.id === "services") ||
                        (ownerIpenNotaryId &&
                          commissionErrors[ownerIpenNotaryId]))
                    }
                    required
                  >
                    <InputLabel id="owner-ipen-notary-select-label">
                      Select Notary
                    </InputLabel>
                    <Select
                      labelId="owner-ipen-notary-select-label"
                      id="owner-ipen-notary-select"
                      value={ownerIpenNotaryId}
                      label="Select Notary"
                      onChange={(e) => setOwnerIpenNotaryId(e.target.value)}
                      disabled={notaryPaymentLoading}
                      sx={{
                        "& .MuiSelect-select": {
                          whiteSpace: "normal !important",
                          wordBreak: "break-word !important",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200, // Set the desired max height for the dropdown menu
                            width: "auto",
                            maxWidth: "100%",
                          },
                        },
                      }}
                    >
                      {orgNotarySubscriptions
                        ?.filter(
                          (sub) =>
                            sub.is_subuser === false || sub.is_subuser === true,
                        )
                        .map((subscription) => (
                          <MenuItem
                            key={subscription.user_id}
                            value={subscription.user_id}
                            sx={{
                              whiteSpace: "normal", // Allow text to wrap
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            {subscription.full_name}
                          </MenuItem>
                        ))}
                    </Select>
                    {ownerNotarizationType === "ipen" &&
                      hasOrgNotaries &&
                      wizardSteps[currentWizardStep]?.id === "services" && (
                        <FormHelperText>
                          {!ownerIpenNotaryId
                            ? "Please select a notary for IPEN notarization"
                            : commissionErrors[ownerIpenNotaryId]}
                        </FormHelperText>
                      )}
                  </FormControl>
                  <FormControl component="fieldset" sx={{ mt: 2 }}>
                    <FormLabel
                      component="legend"
                      sx={{ fontSize: "0.85rem", color: "text.secondary" }}
                    >
                      Require IPEN PIN
                    </FormLabel>
                    <RadioGroup
                      row
                      value={ownerRequireIpenPin ? "yes" : "no"}
                      onChange={(e) =>
                        setOwnerRequireIpenPin(e.target.value === "yes")
                      }
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio size="small" />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio size="small" />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}

              {!hasOrgNotaries && (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 2,
                    borderRadius: "8px",
                    padding: mobileSize ? "8px !important" : undefined,
                  }}
                >
                  <Typography variant="body2">
                    IPEN requires organization notaries. Permit Rockstar
                    notaries cannot perform IPEN notarization. Please add
                    certified notaries to your organization to enable this
                    option.
                  </Typography>
                </Alert>
              )}
            </Card>
          </Grid>
        )}

        {!ownerSigning?.notarizationNeeded &&
          !contractorSigning?.notarizationNeeded && (
            <Grid
              size={{ xs: 12 }}
              sx={{
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                marginTop: 13,
                textAlign: "center",
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 64, mb: 2, opacity: 0.4 }} />
              <Typography variant="h5" fontWeight="bold" color="primary">
                No Notarization Required
              </Typography>
              <Typography
                variant="subtitle1"
                fontWeight="meduim"
                color="text.secondary"
              >
                None of the selected documents require notarization. You can
                proceed to the next step.
              </Typography>
            </Grid>
          )}
      </Grid>
    </AnimatedStep>
  );

  const renderInformationStep = () => (
    <AnimatedStep>
      <Typography
        variant="h6"
        align="center"
        color="primary"
        fontWeight="bold"
        mb={0}
      >
        Signer Information
      </Typography>

      <Typography align="center" color="text.secondary" variant="body2" mb={2}>
        {!contractorSigning?.notarizationNeeded &&
        !contractorSigning?.signingNeeded
          ? "Overview of the signer"
          : "Overview of the signers"}
      </Typography>

      <Grid container spacing={3}>
        {contractorSigning?.notarizationNeeded ||
        contractorSigning?.signingNeeded ? (
          <Grid
            size={{ sm: 12, md: 6 }}
            sx={{
              height: tabletSize ? "100%" : undefined,
              width: mobileSize ? "100%" : undefined,
            }}
          >
            <Card
              sx={{
                p: mobileSize ? 1.5 : 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                background:
                  signingOrder === "contractor"
                    ? "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
                    : "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)",
                borderRadius: "20px",
                border: "1px solid rgba(25, 118, 210, 0.1)",
                boxShadow:
                  "0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(25, 118, 210, 0.05) inset",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    signingOrder === "contractor"
                      ? "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.05) 100%)"
                      : "linear-gradient(135deg, rgba(25, 118, 210, 0.03) 0%, rgba(25, 118, 210, 0) 100%)",
                  pointerEvents: "none",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                },
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow:
                    "0 12px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(25, 118, 210, 0.1) inset",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flex: 1,
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    variant="h5"
                    sx={{
                      color: "primary.main",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    Contractor
                  </Typography>
                  {/* <Typography variant="caption" color="text.secondary" sx={{ position: "relative", zIndex: 1 }}>
                    {isStJohnsCounty() && requiredContractors.length > 0
                      ? requiredContractors[currentContractorIndex]?.businessName ||
                        requiredContractors[currentContractorIndex]?.dba_name ||
                        ""
                      : selectedRow?.contractor?.businessName ||
                        selectedRow?.contractor?.dba_name ||
                        ""}
                  </Typography> */}
                </Box>
                {isStJohnsCounty() && requiredContractors.length > 1 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const prevIndex =
                          currentContractorIndex > 0
                            ? currentContractorIndex - 1
                            : requiredContractors.length - 1;
                        setContractorsSeen((prev) => {
                          const newSet = new Set(prev);
                          newSet.add(prevIndex);
                          return newSet;
                        });
                        setCurrentContractorIndex(prevIndex);
                        setSubmitError(""); // Clear error on navigation
                      }}
                      sx={{
                        color: "primary.main",
                        "&:hover": {
                          backgroundColor: "rgba(0, 150, 136, 0.1)",
                        },
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: "40px", textAlign: "center" }}
                    >
                      {currentContractorIndex + 1} /{" "}
                      {requiredContractors.length}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const nextIndex =
                          currentContractorIndex <
                          requiredContractors.length - 1
                            ? currentContractorIndex + 1
                            : 0;
                        setContractorsSeen((prev) => {
                          const newSet = new Set(prev);
                          newSet.add(currentContractorIndex);
                          newSet.add(nextIndex);
                          // Clear error if all contractors are now seen
                          if (newSet.size >= requiredContractors.length) {
                            setSubmitError("");
                          }
                          return newSet;
                        });
                        setCurrentContractorIndex(nextIndex);
                      }}
                      sx={{
                        color:
                          submitError &&
                          requiredContractors.length > 1 &&
                          contractorsSeen.size < requiredContractors.length
                            ? "error.main"
                            : "primary.main",
                        "&:hover": {
                          backgroundColor:
                            submitError &&
                            requiredContractors.length > 1 &&
                            contractorsSeen.size < requiredContractors.length
                              ? "rgba(211, 47, 47, 0.1)"
                              : "rgba(0, 150, 136, 0.1)",
                        },
                      }}
                    >
                      <ArrowForwardIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>

              <Stack
                spacing={2}
                sx={{ position: "relative", zIndex: 1, flex: 1 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexDirection: extraSmall ? "column" : "row",
                  }}
                >
                  <TextField
                    ref={refs.cFirst}
                    disabled
                    size="small"
                    error={!!(formik.touched.cFirst && formik.errors.cFirst)}
                    fullWidth
                    helperText={formik.touched.cFirst && formik.errors.cFirst}
                    name="cFirst"
                    value={formik.values.cFirst || ""}
                    label="First Name"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                      },
                    }}
                  />
                  <TextField
                    disabled
                    size="small"
                    error={!!(formik.touched.cMiddle && formik.errors.cMiddle)}
                    fullWidth
                    helperText={formik.touched.cMiddle && formik.errors.cMiddle}
                    name="cMiddle"
                    value={formik.values.cMiddle || ""}
                    label="Middle Name"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                      },
                    }}
                  />
                  <TextField
                    ref={refs.cLast}
                    disabled
                    size="small"
                    error={!!(formik.touched.cLast && formik.errors.cLast)}
                    fullWidth
                    helperText={formik.touched.cLast && formik.errors.cLast}
                    name="cLast"
                    value={formik.values.cLast || ""}
                    label="Last Name"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                      },
                    }}
                  />
                </Box>

                {contractorSigning?.notarizationNeeded && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexDirection: extraSmall ? "column" : "row",
                    }}
                  >
                    <TextField
                      ref={refs.cAddress}
                      disabled={notaryPaymentLoading}
                      size="small"
                      error={
                        !!(formik.touched.cAddress && formik.errors.cAddress)
                      }
                      fullWidth
                      helperText={
                        formik.touched.cAddress && formik.errors.cAddress
                      }
                      name="cAddress"
                      value={formik.values.cAddress || ""}
                      onChange={formik.handleChange}
                      label="Address"
                      variant="filled"
                      sx={{
                        "& .MuiFilledInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        },
                      }}
                    />
                    <TextField
                      ref={refs.cCity}
                      disabled={notaryPaymentLoading}
                      size="small"
                      error={!!(formik.touched.cCity && formik.errors.cCity)}
                      fullWidth
                      helperText={formik.touched.cCity && formik.errors.cCity}
                      name="cCity"
                      value={formik.values.cCity || ""}
                      onChange={formik.handleChange}
                      label="City"
                      variant="filled"
                      sx={{
                        "& .MuiFilledInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        },
                      }}
                    />
                    <TextField
                      ref={refs.cZip}
                      disabled={notaryPaymentLoading}
                      size="small"
                      error={!!(formik.touched.cZip && formik.errors.cZip)}
                      fullWidth
                      helperText={formik.touched.cZip && formik.errors.cZip}
                      name="cZip"
                      value={formik.values.cZip || ""}
                      onChange={formik.handleChange}
                      label="ZIP"
                      variant="filled"
                      sx={{
                        "& .MuiFilledInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        },
                      }}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexDirection: extraSmall ? "column" : "row",
                  }}
                >
                  <TextField
                    ref={refs.cEmail}
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={!!(formik.touched.cEmail && formik.errors.cEmail)}
                    fullWidth
                    helperText={formik.touched.cEmail && formik.errors.cEmail}
                    name="cEmail"
                    value={formik.values.cEmail || ""}
                    onChange={formik.handleChange}
                    label="Email"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                  <TextField
                    ref={refs.cMobile}
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={!!(formik.touched.cMobile && formik.errors.cMobile)}
                    fullWidth
                    helperText={formik.touched.cMobile && formik.errors.cMobile}
                    name="cMobile"
                    value={formik.values.cMobile || ""}
                    label="Mobile"
                    variant="filled"
                    onChange={(e) => handleNewPhoneChange(e)}
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                </Box>
              </Stack>

              {/* Signing Order Button */}
              {(ownerSigning?.notarizationNeeded ||
                ownerSigning?.signingNeeded) &&
                (contractorSigning?.notarizationNeeded ||
                  contractorSigning?.signingNeeded) && (
                  <Box
                    sx={{
                      mt: "auto",
                      pt: 2,
                      display: "flex",
                      justifyContent: "flex-start",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <Button
                      disabled={
                        notaryPaymentLoading ||
                        selectedRow?.documents?.noc_affidavit?.is_created ===
                          true
                      }
                      onClick={() => setSigningOrder("contractor")}
                      variant={
                        signingOrder === "contractor" ? "contained" : "outlined"
                      }
                      size="medium"
                      fullWidth
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 2,
                        py: 0.75,
                        fontWeight:
                          signingOrder === "contractor" ? "bold" : "medium",
                        backgroundColor:
                          signingOrder === "contractor"
                            ? "primary.main"
                            : "transparent",
                        color:
                          signingOrder === "contractor"
                            ? "white"
                            : "primary.main",
                        borderColor: "primary.main",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            signingOrder === "contractor"
                              ? "primary.dark"
                              : "rgba(25, 118, 210, 0.08)",
                          color:
                            signingOrder === "contractor"
                              ? "white"
                              : "primary.main",
                          borderColor:
                            signingOrder === "contractor"
                              ? "primary.dark"
                              : "primary.main",
                        },
                      }}
                    >
                      Contractor Sign/Notarize First
                    </Button>
                  </Box>
                )}
            </Card>
          </Grid>
        ) : null}

        <Grid
          size={{
            sm: 12,
            md:
              contractorSigning?.notarizationNeeded ||
              contractorSigning?.signingNeeded
                ? 6
                : 12,
          }}
          sx={{
            height: tabletSize ? "100%" : undefined,
            width: mobileSize ? "100%" : undefined,
          }}
        >
          {ownerSigning?.notarizationNeeded || ownerSigning?.signingNeeded ? (
            <Card
              sx={{
                p: mobileSize ? 1.5 : 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                background:
                  signingOrder === "owner"
                    ? "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
                    : "linear-gradient(135deg, #fafbfc 0%, #f0f2f5 100%)",
                borderRadius: "20px",
                border: "1px solid rgba(25, 118, 210, 0.1)",
                boxShadow:
                  "0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(25, 118, 210, 0.05) inset",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    signingOrder === "owner"
                      ? "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.05) 100%)"
                      : "linear-gradient(135deg, rgba(25, 118, 210, 0.03) 0%, rgba(25, 118, 210, 0) 100%)",
                  pointerEvents: "none",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                },
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow:
                    "0 12px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(25, 118, 210, 0.1) inset",
                },
              }}
            >
              <Typography
                fontWeight="bold"
                align="center"
                mb={3}
                variant="h5"
                sx={{ color: "primary.main", position: "relative", zIndex: 1 }}
              >
                Owner/Agent
              </Typography>

              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexDirection: extraSmall ? "column" : "row",
                  }}
                >
                  <TextField
                    ref={refs.oFirst}
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={
                      getOwnerNameFieldError(
                        formik,
                        "oFirst",
                        ownerInfoRequired,
                      ) ||
                      (ownerInfoRequired &&
                        hasInvalidOwnerNameValue(formik.values.oFirst))
                    }
                    fullWidth
                    helperText={
                      hasInvalidOwnerNameValue(formik.values.oFirst)
                        ? OWNER_NAME_ERROR_MESSAGE
                        : getOwnerNameFieldHelperText(
                            formik,
                            "oFirst",
                            ownerInfoRequired,
                          )
                    }
                    name="oFirst"
                    value={formik.values.oFirst || ""}
                    onChange={handleOwnerNameChange}
                    label="First Name"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                  <TextField
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={
                      getOwnerNameFieldError(
                        formik,
                        "oMiddle",
                        ownerInfoRequired,
                      ) ||
                      (ownerInfoRequired &&
                        hasInvalidOwnerNameValue(formik.values.oMiddle))
                    }
                    fullWidth
                    helperText={
                      hasInvalidOwnerNameValue(formik.values.oMiddle)
                        ? OWNER_NAME_ERROR_MESSAGE
                        : getOwnerNameFieldHelperText(
                            formik,
                            "oMiddle",
                            ownerInfoRequired,
                          )
                    }
                    name="oMiddle"
                    value={formik.values.oMiddle || ""}
                    onChange={handleOwnerNameChange}
                    label="Middle Name"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                  <TextField
                    ref={refs.oLast}
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={
                      getOwnerNameFieldError(
                        formik,
                        "oLast",
                        ownerInfoRequired,
                      ) ||
                      (ownerInfoRequired &&
                        hasInvalidOwnerNameValue(formik.values.oLast))
                    }
                    fullWidth
                    helperText={
                      hasInvalidOwnerNameValue(formik.values.oLast)
                        ? OWNER_NAME_ERROR_MESSAGE
                        : getOwnerNameFieldHelperText(
                            formik,
                            "oLast",
                            ownerInfoRequired,
                          )
                    }
                    name="oLast"
                    value={formik.values.oLast || ""}
                    onChange={handleOwnerNameChange}
                    label="Last Name"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                </Box>

                {ownerSigning?.notarizationNeeded && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexDirection: extraSmall ? "column" : "row",
                    }}
                  >
                    <TextField
                      ref={refs.oAddress}
                      disabled={notaryPaymentLoading}
                      size="small"
                      error={
                        !!(formik.touched.oAddress && formik.errors.oAddress)
                      }
                      fullWidth
                      helperText={
                        formik.touched.oAddress && formik.errors.oAddress
                      }
                      name="oAddress"
                      value={formik.values.oAddress || ""}
                      onChange={formik.handleChange}
                      label="Address"
                      variant="filled"
                      sx={{
                        "& .MuiFilledInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        },
                      }}
                    />
                    <TextField
                      ref={refs.oCity}
                      disabled={notaryPaymentLoading}
                      size="small"
                      error={!!(formik.touched.oCity && formik.errors.oCity)}
                      fullWidth
                      helperText={formik.touched.oCity && formik.errors.oCity}
                      name="oCity"
                      value={formik.values.oCity || ""}
                      onChange={formik.handleChange}
                      label="City"
                      variant="filled"
                      sx={{
                        "& .MuiFilledInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        },
                      }}
                    />
                    <TextField
                      ref={refs.oZip}
                      disabled={notaryPaymentLoading}
                      size="small"
                      error={!!(formik.touched.oZip && formik.errors.oZip)}
                      fullWidth
                      helperText={formik.touched.oZip && formik.errors.oZip}
                      name="oZip"
                      value={formik.values.oZip || ""}
                      onChange={formik.handleChange}
                      label="ZIP"
                      variant="filled"
                      sx={{
                        "& .MuiFilledInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        },
                      }}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexDirection: extraSmall ? "column" : "row",
                  }}
                >
                  <TextField
                    ref={refs.oEmail}
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={!!(formik.touched.oEmail && formik.errors.oEmail)}
                    fullWidth
                    helperText={formik.touched.oEmail && formik.errors.oEmail}
                    name="oEmail"
                    value={formik.values.oEmail || ""}
                    onChange={formik.handleChange}
                    label="Email"
                    variant="filled"
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                  <TextField
                    ref={refs.oMobile}
                    disabled={notaryPaymentLoading}
                    size="small"
                    error={!!(formik.touched.oMobile && formik.errors.oMobile)}
                    fullWidth
                    helperText={formik.touched.oMobile && formik.errors.oMobile}
                    name="oMobile"
                    value={formik.values.oMobile || ""}
                    label="Mobile"
                    variant="filled"
                    onChange={(e) => handleNewPhoneChange(e)}
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      name="oMobileConsent"
                      checked={formik.values.oMobileConsent}
                      onChange={formik.handleChange}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I confirm I have the homeowner's permission to send text
                      messages to the mobile number above. Data processing
                      follows our{" "}
                      <Link
                        href="https://permitrockstar.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="primary"
                        sx={{ textDecoration: "underline", fontWeight: "bold" }}
                      >
                        Privacy Policy
                      </Link>
                      .
                    </Typography>
                  }
                />

                {/* <Typography
                  variant="body2"
                  color="primary"
                  fontWeight="bold"
                  align="center"
                  sx={{ mt: 1 }}
                >
                  {ownerSigning.notarizationNeeded
                    ? "All other information will be gathered from the signer. Please ensure that the contact information you enter is correct."
                    : "Please ensure that you enter the owner's full personal name and other required details accurately, as this information will be used to initiate the signing process."}
                </Typography> */}
              </Stack>

              {/* Signing Order Button */}
              {(ownerSigning?.notarizationNeeded ||
                ownerSigning?.signingNeeded) &&
                (contractorSigning?.notarizationNeeded ||
                  contractorSigning?.signingNeeded) && (
                  <Box
                    sx={{
                      mt: "auto",
                      pt: 2,
                      display: "flex",
                      justifyContent: "flex-start",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <Button
                      disabled={notaryPaymentLoading}
                      onClick={() => setSigningOrder("owner")}
                      variant={
                        signingOrder === "owner" ? "contained" : "outlined"
                      }
                      size="medium"
                      fullWidth
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 2,
                        py: 0.75,
                        fontWeight:
                          signingOrder === "owner" ? "bold" : "medium",
                        backgroundColor:
                          signingOrder === "owner"
                            ? "primary.main"
                            : "transparent",
                        color:
                          signingOrder === "owner" ? "white" : "primary.main",
                        borderColor: "primary.main",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            signingOrder === "owner"
                              ? "primary.dark"
                              : "rgba(25, 118, 210, 0.08)",
                          color:
                            signingOrder === "owner" ? "white" : "primary.main",
                          borderColor:
                            signingOrder === "owner"
                              ? "primary.dark"
                              : "primary.main",
                        },
                      }}
                    >
                      Owner/Agent Sign/Notarize First
                    </Button>
                  </Box>
                )}
            </Card>
          ) : (
            <Card
              sx={{
                p: mobileSize ? 1.5 : 3,
                textAlign: "center",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body2" color="primary" fontWeight="bold">
                {contractorSigning?.notarizationNeeded ||
                contractorSigning?.signingNeeded
                  ? contractorSigning?.notarizationNeeded
                    ? "All other information will be gathered from the signer. Please ensure that the contact information you enter is correct."
                    : "Please ensure that you enter the contractor's full personal name and other required details accurately, as this information will be used to initiate the signing process."
                  : "No additional signer information is required."}
              </Typography>
            </Card>
          )}
        </Grid>
      </Grid>
    </AnimatedStep>
  );

  const renderPaymentStep = () => (
    <AnimatedStep>
      <Typography
        variant="h6"
        align="center"
        color="primary"
        fontWeight="bold"
        mb={2}
      >
        Payment & Confirmation
      </Typography>

      {/* IPEN PIN Display */}
      {notarizationType === "ipen" && ipenPin && (
        <Card
          sx={{
            p: mobileSize ? 0.5 : 2,
            bgcolor: "#e3f2fd",
            border: "2px solid #2196f3",
            mb: mobileSize ? 1.5 : 3,
          }}
        >
          <Typography
            variant="subtitle1"
            color="primary"
            fontWeight="bold"
            align="center"
            mb={1}
          >
            📌 Verification PIN
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontFamily: "monospace",
              letterSpacing: 4,
              textAlign: "center",
              color: "#1976d2",
              fontWeight: "bold",
              mb: 1,
            }}
          >
            {ipenPin}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Provide this PIN to your notary to verify physical presence
          </Typography>
        </Card>
      )}

      {/* Mixed Session Warning */}
      {notarizationType === "ipen" &&
        ownerSigning.notarizationNeeded &&
        contractorSigning.notarizationNeeded && (
          <Alert
            severity="info"
            sx={{ mb: mobileSize ? 1.5 : 3, p: mobileSize ? 1 : undefined }}
          >
            <Typography variant="body2">
              ℹ️ <strong>Note:</strong> Since both owner and contractor
              notarization is required, contractor documents will use IPEN while
              owner documents will be processed separately via RON.
            </Typography>
          </Alert>
        )}

      {/* Payment Section */}
      {!selectedRow?.payments?.vip?.paid && (
        <Grid container spacing={3}>
          <Grid
            size={{ sm: 12, md: 6 }}
            sx={{ height: tabletSize ? "100%" : undefined }}
          >
            <Card
              sx={{
                p: mobileSize ? 1 : 3,
                height: "100%",
                background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)",
                borderRadius: "20px",
                border: "1px solid rgba(25, 118, 210, 0.1)",
                boxShadow:
                  "0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(25, 118, 210, 0.05) inset",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(135deg, rgba(25, 118, 210, 0.03) 0%, rgba(25, 118, 210, 0) 100%)",
                  pointerEvents: "none",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow:
                    "0 12px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(25, 118, 210, 0.1) inset",
                },
              }}
            >
              <Typography
                align="center"
                variant="h5"
                mb={3}
                sx={{
                  color: "primary.main",
                  fontWeight: "bold",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {!isNoChargeUser
                  ? isZeroTotalByCoupon
                    ? "Payment Method (No Charge)"
                    : "Select Payment Card"
                  : "No Payment Card Required"}
              </Typography>

              {currentUser?.user?.role !== "admin" &&
                !currentUser?.user?.admin &&
                isZeroTotalByCoupon && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This notarization is free with your coupon. Please select a
                    card for Stripe verification — you will not be charged.
                  </Alert>
                )}

              {paymentCards?.length === 0 &&
              !isAdminUser &&
              !isRemoteConnectSession &&
              !isCustomerPackagesAdminFlow &&
              !isOrgUser ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="error"
                    mb={2}
                  >
                    You do not have any payment method saved. Please click the
                    button below to navigate to the payments page and set up a
                    payment method.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      await dispatch(
                        setModalsState({ addPaymentModalOpen: true }),
                      );
                    }}
                  >
                    Set up card payment
                  </Button>
                </Box>
              ) : isCustomerPackagesAdminFlow ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No payment method is required when an admin starts
                    notarization from Customer Packages. This request will be
                    submitted without charging a card.
                  </Alert>
                </Box>
              ) : isOrgUser &&
                paymentCards?.length === 0 &&
                !isRemoteConnectSession &&
                !isAdminUser ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No payment method is required for Enterprise / Branch
                    accounts. Your total is $0.00.
                  </Alert>
                </Box>
              ) : (
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  {isRemoteConnectSession && !isAdminUser && (
                    <Card sx={{ p: 2, mb: 2, backgroundColor: "#fff8e1" }}>
                      <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                        Remote connection payment action
                      </Typography>
                      {paymentCards?.length > 0 ? (
                        <>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={1.5}
                          >
                            Choose one option for this session. "Charge default
                            card" will charge the user's default card on file.
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ justifyContent: "center", mb: 1 }}
                          >
                            <Button
                              size="small"
                              variant={
                                remotePaymentChoice === "charge_default"
                                  ? "contained"
                                  : "outlined"
                              }
                              onClick={() =>
                                setRemotePaymentChoice("charge_default")
                              }
                            >
                              Charge default card
                            </Button>
                            <Button
                              size="small"
                              variant={
                                remotePaymentChoice === "skip"
                                  ? "contained"
                                  : "outlined"
                              }
                              color="warning"
                              onClick={() => setRemotePaymentChoice("skip")}
                            >
                              Skip payment
                            </Button>
                          </Stack>
                          {defaultPaymentCard?.cardNum && (
                            <Box
                              sx={{ display: "flex", justifyContent: "center" }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Default card: {defaultPaymentCard.cardNum}
                              </Typography>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No card on file. Skip payment will be used
                          automatically.
                        </Typography>
                      )}
                    </Card>
                  )}
                  {!isCustomerPackagesAdminFlow && (
                    <CreditCardDisplay
                      cards={paymentCards || []}
                      activeStep={activeStep}
                      setActiveStep={setActiveStep}
                    />
                  )}
                </Box>
              )}
            </Card>
          </Grid>

          <Grid
            size={{ sm: 12, md: 6 }}
            sx={{ height: tabletSize ? "100%" : undefined }}
          >
            <Card
              sx={{
                p: mobileSize ? 1 : 3,
                height: "100%",
                background: "linear-gradient(135deg, #fafbfc 0%, #f0f2f5 100%)",
                borderRadius: "20px",
                border: "1px solid rgba(25, 118, 210, 0.1)",
                boxShadow:
                  "0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(25, 118, 210, 0.05) inset",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(135deg, rgba(25, 118, 210, 0.03) 0%, rgba(25, 118, 210, 0) 100%)",
                  pointerEvents: "none",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow:
                    "0 12px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(25, 118, 210, 0.1) inset",
                },
              }}
            >
              <Typography
                variant="h5"
                align="center"
                sx={{
                  fontWeight: "bold",
                  mb: 3,
                  color: "primary.main",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                Total Cost
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  p: mobileSize ? 1 : 3,
                  borderRadius: "16px",
                  bgcolor: "rgba(255, 255, 255, 1)",
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    color: "primary.main",
                    textDecoration:
                      (!signOnly && confirmedPricing?.has_discount) ||
                      (!signOnly && discountPricing?.hasDiscount) ||
                      showsZeroTotalWithoutPayment
                        ? "line-through"
                        : "none",
                  }}
                >
                  {formatMoneyValue(
                    signOnly
                      ? currentUser?.service_tiers?.sign_only
                      : confirmedPricing?.original_price ||
                          (docNotaryTypeValue === 0
                            ? currentUser?.service_tiers?.notary_record
                            : docNotaryTypeValue === 1
                              ? currentUser?.service_tiers?.noc_notary
                              : currentUser?.service_tiers
                                  ?.permit_package_notary),
                  )}
                </Typography>
                {confirmedPricing?.has_discount ? (
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "success.main", mt: 1 }}
                  >
                    {Number(confirmedPricing?.final_price) === 0
                      ? "$0.00"
                      : formatMoneyValue(confirmedPricing?.final_price)}
                  </Typography>
                ) : discountPricing?.hasDiscount &&
                  currentUser?.user?.role !== "admin" &&
                  !currentUser?.user?.admin ? (
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "success.main", mt: 1 }}
                  >
                    {Number(discountPricing?.finalPrice) === 0
                      ? "$0.00"
                      : formatMoneyValue(discountPricing?.finalPrice)}
                  </Typography>
                ) : showsZeroTotalWithoutPayment ? (
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "success.main", mt: 1 }}
                  >
                    $0.00
                  </Typography>
                ) : null}
                <Typography
                  align="center"
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 2 }}
                >
                  {isCustomerPackagesAdminFlow
                    ? "Admin customer-package notarization is submitted with no charge and no card required."
                    : (() => {
                        if (signOnly) {
                          return "This is the total amount for Signing for all documents";
                        } else {
                          if (docNotaryTypeValue === 0) {
                            return `This is the total amount for Notarization${hasSelectedNoc ? " & Recording" : ""} for all documents`;
                          }
                          if (docNotaryTypeValue === 1) {
                            return "This is the total amount for Notarization & Recording for Notice of Commencement Form";
                          }
                        }

                        return "This is the total amount for Notarization for all permit documents, excluding the Notice of Commencement";
                      })()}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Auto-Start Recording Option */}
      {docNotaryTypeValue !== 2 && hasSelectedNoc && (
        <Card sx={{ p: mobileSize ? 1 : 2, mt: mobileSize ? "26px" : 5 }}>
          <FormControlLabel
            disabled={notaryPaymentLoading || !nocHasActiveNotarization}
            control={
              <Switch
                checked={autoStartRecording}
                disabled={notaryPaymentLoading || !nocHasActiveNotarization}
                onChange={(e) => setAutoStartRecording(e.target.checked)}
              />
            }
            label="Automatically start recording process after notarization"
          />
          {nocHasActiveNotarization ? (
            <Typography variant="caption" color="text.secondary">
              {autoStartRecording
                ? "Recording will start automatically after notarization."
                : "Recording will need to be started manually after notarization."}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Recording is only available when NOC notarization is included.
            </Typography>
          )}
        </Card>
      )}
    </AnimatedStep>
  );

  // Helper function to handle post-payment actions
  const handlePostPayment = async (resp, helpers) => {
    if (resp?.data?.success && resp?.data?.need_assign) {
      // Auto-switch to "Packages Sent to Contractors" tab when package is assigned
      dispatch(setActiveTab(1));

      const result = await dispatch(
        getPermitPackages({ newPage: 0, newPageSize: 5, customActiveTab: 1 }),
      );
      dispatch(setSelectedRow(null));
      dispatch(setPage(0));
      dispatch(setRowCount(result?.data || 0));
      setSnackOpen(true);
    } else if (resp?.data?.success && !resp?.data?.need_assign) {
      await dispatch(updatePermitPackage(resp?.data?.data));
      setSnackOpen(true);
      setOpen(false);

      if (
        !resp?.data?.onlyOwner &&
        resp?.data?.data?.processing_permit?.notarization?.signing_order !==
          "owner"
      ) {
        setRonScheduleOpen(true);
      }
      setIsAcknowledged(false);
      helpers?.resetForm();
    }
  };

  // Get current step content
  const getCurrentStepContent = () => {
    const currentStep = wizardSteps[currentWizardStep];
    if (!currentStep) return renderOverviewStep();

    switch (currentStep.id) {
      case "overview":
        return renderOverviewStep();
      case "method":
        return renderMethodStep();
      case "notary":
        return renderNotaryStep();
      case "services":
        return renderServicesStep();
      case "information":
        return renderInformationStep();
      case "payment":
        return renderPaymentStep();
      default:
        return renderOverviewStep();
    }
  };

  return (
    <>
      <AnimatedModal
        open={open}
        onClose={() => {
          // setOpen(false);
          // resetModalState(formik);
        }}
      >
        <ModalContainer className="modal-materialize" isMobile={mobileSize}>
          {/* Header */}
          <ModalHeader isMobile={mobileSize}>
            <IconButton
              disabled={notaryPaymentLoading}
              onClick={() => {
                setOpen(false);
                resetModalState(formik);
                setCurrentWizardStep(0);
                setOwnerIpenNotaryId("");
                setNotarizationType("ron");
                setOwnerRonNotarySource("permitRockstar");
                setContractorRonNotarySource("permitRockstar");
                setIpenPin("");
                setOwnerNotarizationType("ron");
                setOwnerRequireIpenPin(true);
                setContractorNotarizationType("ron");
                setContractorIpenNotaryId("");
                setContractorRequireIpenPin(true);
                setContractorSettings({}); // Reset all contractor settings
                // Reset refs when modal closes
                notarizationTypeRef.current = "ron";
                ownerNotarizationTypeRef.current = "ron";
                ownerRonNotarySourceRef.current = "permitRockstar";
                contractorRonNotarySourceRef.current = "permitRockstar";
                ipenPinRef.current = "";
                ownerIpenNotaryIdRef.current = "";
                setPlanReviewService({
                  usePermitRockstar: false,
                  useOrganization: false,
                  customProvider: "",
                });
                setInspectionService({
                  usePermitRockstar: false,
                  useOrganization: false,
                  customProvider: "",
                  inspectionTypes: [],
                });
                setOwnerSigning({
                  notarizationNeeded: false,
                  signingNeeded: false,
                });
                setContractorSigning({
                  notarizationNeeded: false,
                  signingNeeded: false,
                });
                setLoadingNotaries(false);
                setSignOnly(false);
                setConfirmedPricing(null);
              }}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "inherit",
                zIndex: 10,
                padding: 1,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            <Typography
              variant="h5"
              align="center"
              fontWeight="bold"
              sx={{
                mb: 0.5,
                position: "relative",
                zIndex: 1,
                mr: extraSmall ? 3 : 0,
              }}
            >
              Notarization & Signing Portal
            </Typography>
            <Typography
              variant="body2"
              align="center"
              sx={{ opacity: 0.9, position: "relative", zIndex: 1, mb: 1.5 }}
            >
              Complete your document signing and notarization in minutes
            </Typography>

            <Paper
              sx={{
                p: 1,
                bgcolor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                borderRadius: 1.5,
                position: "relative",
                zIndex: 1,
              }}
            >
              <Typography
                variant="body2"
                align="center"
                fontWeight="600"
                sx={{ color: "#ffb74d" }}
              >
                {docNotaryTypeValue === 0
                  ? "All Documents Selected"
                  : docNotaryTypeValue === 1 &&
                      selectedRow?.documents?.noc_affidavit?.is_created === true
                    ? "Notice of Commencement + Affidavit"
                    : docNotaryTypeValue === 1
                      ? "Notice of Commencement Only"
                      : "All Permit Forms (Excluding NOC)"}
              </Typography>
            </Paper>
          </ModalHeader>

          {/* Progress Indicator */}
          <ProgressIndicator isMobile={mobileSize}>
            <Stepper
              activeStep={currentWizardStep}
              orientation="horizontal"
              sx={{
                width: "100%",
                flexWrap: tabletSize ? "wrap" : "nowrap",
                rowGap: 1,
                "& .MuiStepConnector-root": {
                  minWidth: tabletSize ? undefined : "10px",
                },
              }}
            >
              {wizardSteps.map((step, index) => (
                <Step key={step.id}>
                  <StepLabel
                    StepIconComponent={
                      tabletSize
                        ? null
                        : ({ active, completed }) => (
                            <StepIcon
                              completed={completed}
                              active={active}
                              onClick={() => handleStepClick(index)}
                            >
                              {completed ? <CheckCircleIcon /> : step.icon}
                            </StepIcon>
                          )
                    }
                    onClick={() => handleStepClick(index)}
                    sx={{ cursor: "pointer" }}
                  >
                    {!mobileSize && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight:
                            currentWizardStep === index ? "bold" : "normal",
                        }}
                      >
                        {step.title}
                      </Typography>
                    )}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </ProgressIndicator>

          {/* Main Content */}
          <Box
            sx={{
              p: mobileSize ? 1.5 : 2,
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {/* Step Content */}
            <Box>{getCurrentStepContent()}</Box>
          </Box>

          {/* Navigation */}
          <Divider />

          {/* Declaration Section - Only show on payment step */}
          {wizardSteps[currentWizardStep]?.id === "payment" && (
            <Box
              sx={{
                px: mobileSize ? 1 : 2,
                pt: mobileSize ? 1 : 2,
                pb: mobileSize ? 1 : 0,
              }}
            >
              <FormControlLabel
                disabled={notaryPaymentLoading}
                sx={{ marginRight: mobileSize ? 0 : 2 }}
                control={
                  <Checkbox
                    color="primary"
                    checked={isAcknowledged}
                    onChange={(event) =>
                      setIsAcknowledged(event.target.checked)
                    }
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    I acknowledge that I have reviewed the forms I created, and
                    understand that once notarization/signing begins, no further
                    modifications can be made to the documents.
                  </Typography>
                }
              />
            </Box>
          )}

          {submitError && (
            <Typography
              variant="body2"
              color="error"
              pt={1}
              sx={{ fontWeight: "bold", textAlign: "center" }}
            >
              {submitError}
            </Typography>
          )}

          <Box
            sx={{
              p: 1.5,
            }}
          >
            {wizardSteps[currentWizardStep]?.id === "information" && (
              <Alert severity="warning" sx={{ mb: 1, borderRadius: "8px" }}>
                <Typography variant="body2">
                  Please review your details carefully. You will not be able to
                  modify this information after submitting.
                </Typography>
              </Alert>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                gap: mobileSize ? 1 : 1.5,
                flexShrink: 0,
              }}
            >
              <FloatingActionButton
                variant="outlined"
                disabled={currentWizardStep === 0 || notaryPaymentLoading}
                onClick={handlePrevious}
                startIcon={<ArrowBackIcon />}
                size={mobileSize ? "small" : "medium"}
              >
                Previous
              </FloatingActionButton>

              {/* Document Review Link */}
              {wizardSteps[currentWizardStep]?.id === "payment" && (
                <Box>
                  <FloatingActionButton
                    variant="text"
                    onClick={() => setReviewDocModalOpen(true)}
                    size={mobileSize ? "small" : "medium"}
                  >
                    📄 Review documents
                  </FloatingActionButton>
                </Box>
              )}

              <FloatingActionButton
                variant="contained"
                disabled={
                  (wizardSteps[currentWizardStep]?.id === "services" &&
                    ((ownerSigning?.notarizationNeeded &&
                      ownerNotarizationType === "ipen" &&
                      hasOrgNotaries &&
                      !ownerIpenNotaryId) ||
                      (contractorSigning?.notarizationNeeded &&
                        hasOrgNotaries &&
                        requiredContractors.some((contractor) => {
                          const license = contractor?.licenseNumber;
                          if (!license) return false;
                          const settings = contractorSettings[license];
                          return (
                            settings?.notarizationType === "ipen" &&
                            !settings?.ipenNotaryId
                          );
                        })))) ||
                  (wizardSteps[currentWizardStep]?.id === "information" &&
                    !isSignerInformationValid()) ||
                  (wizardSteps[currentWizardStep]?.id === "payment" &&
                    !isAcknowledged) ||
                  notaryPaymentLoading ||
                  (!isAdminUser &&
                    !isRemoteConnectSession &&
                    !isCustomerPackagesAdminFlow &&
                    !isOrgUser &&
                    paymentCards.length === 0 &&
                    wizardSteps[currentWizardStep]?.id === "payment") ||
                  (isRemoteConnectSession &&
                    !isAdminUser &&
                    paymentCards.length > 0 &&
                    !remotePaymentChoice &&
                    wizardSteps[currentWizardStep]?.id === "payment")
                }
                onClick={async () => {
                  if (currentWizardStep < wizardSteps.length - 1) {
                    if (wizardSteps[currentWizardStep]?.id === "information") {
                      if (isEmailConflict) {
                        formik.setFieldTouched("oEmail", true, false);
                        formik.setFieldTouched("cEmail", true, false);
                        formik.setFieldError("oEmail", EMAIL_CONFLICT_MESSAGE);
                        formik.setFieldError("cEmail", EMAIL_CONFLICT_MESSAGE);
                        scrollToFirstError({ cEmail: EMAIL_CONFLICT_MESSAGE });
                        return;
                      }

                      const errors = await formik?.validateForm();
                      if (errors && Object.keys(errors).length > 0) {
                        // Set errors in formik so TextFields can display them
                        formik.setErrors(errors);
                        // Mark all fields with errors as touched so they show error state
                        const touchedFields = {};
                        Object.keys(errors).forEach((field) => {
                          touchedFields[field] = true;
                        });
                        formik.setTouched(touchedFields);
                        scrollToFirstError(errors);
                        // setSubmitError(
                        //   `Some fields are incomplete: ${Object.keys(errors).join(", ")}. Please review the form carefully before proceeding.`
                        // );
                        return;
                      }
                    }
                    handleNext();
                  } else {
                    // // Final submission
                    // const errors = await formik?.validateForm();
                    // if (errors && Object.keys(errors).length > 0) {
                    //   // Set errors in formik so TextFields can display them
                    //   formik.setErrors(errors);
                    //   // Mark all fields with errors as touched so they show error state
                    //   const touchedFields = {};
                    //   Object.keys(errors).forEach((field) => {
                    //     touchedFields[field] = true;
                    //   });
                    //   formik.setTouched(touchedFields);
                    //   scrollToFirstError(errors);
                    //   setSubmitError(
                    //     `Some fields are incomplete: ${Object.keys(errors).join(", ")}. Please review the form carefully before proceeding.`,
                    //   );
                    //   return;
                    // }
                    // formik.handleSubmit();

                    setOpenConfirmationModal(true);
                  }
                }}
                loading={notaryPaymentLoading}
                endIcon={
                  currentWizardStep < wizardSteps.length - 1 ? (
                    <ArrowForwardIcon />
                  ) : (
                    <SendIcon />
                  )
                }
                size={mobileSize ? "small" : "medium"}
              >
                {currentWizardStep === wizardSteps.length - 1
                  ? !selectedRow?.payments?.vip?.paid &&
                    !ownerSigning.notarizationNeeded &&
                    !contractorSigning.notarizationNeeded
                    ? "Submit Document(s) Signing"
                    : !selectedRow?.payments?.vip?.paid &&
                        (ownerSigning.notarizationNeeded ||
                          contractorSigning.notarizationNeeded)
                      ? "Submit Notarization Request"
                      : "Submit Purchase"
                  : "Next"}
              </FloatingActionButton>
            </Box>
          </Box>
        </ModalContainer>
      </AnimatedModal>
      <NotarizationRequestConfirmationModal
        open={openConfirmationModal}
        setOpen={setOpenConfirmationModal}
        loading={notaryPaymentLoading}
        onConfirm={async () => {
          // Final submission
          const errors = await formik?.validateForm();
          if (errors && Object.keys(errors).length > 0) {
            // Set errors in formik so TextFields can display them
            formik.setErrors(errors);
            // Mark all fields with errors as touched so they show error state
            const touchedFields = {};
            Object.keys(errors).forEach((field) => {
              touchedFields[field] = true;
            });
            formik.setTouched(touchedFields);
            scrollToFirstError(errors);
            setSubmitError(
              `Some fields are incomplete: ${Object.keys(errors).join(", ")}. Please review the form carefully before proceeding.`,
            );
            return;
          }
          formik.handleSubmit();
        }}
      />
    </>
  );
}

export default React.memo(StartNotarizationModal);
